import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import useTabInPath, { tabForSlug } from '../hooks/useTabInPath';
import ExpenseEntryCard from '../components/ExpenseEntryCard';
import ReceiptImageEditor from '../components/ReceiptImageEditor';
import { useDebug } from '../contexts/DebugContext';
import { useAuth } from '../contexts/AuthContext';
import { displayDate } from '../utils/dates';
import { IconButton, Banner } from '../components/ui';

// A paid expense report shows "Paid MM/DD/YYYY" (from PaidDate) instead of its
// underlying "Approved" status.
function reportStatusLabel(r) {
  if (r && r.PaidDate) { const f = displayDate(r.PaidDate); return f ? `Paid ${f}` : 'Paid'; }
  return r ? r.Status : '';
}
function reportStatusClass(r) {
  return (r && r.PaidDate) ? 'paid' : ((r && r.Status) || '').toLowerCase();
}

const isMobile = /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

function formatCurrency(val) {
  if (val == null) return '$0.00';
  return `$${parseFloat(val).toFixed(2)}`;
}

// Check if an entry is effectively empty (only has date or nothing)
function isEntryEmpty(e) {
  if (!e) return true;
  const hasAmount = e.TotalPrice && parseFloat(e.TotalPrice) !== 0;
  const hasDesc = !!e.Description;
  const hasJob = !!e.JobID;
  const hasRate = !!e.RateCodeID;
  return !hasAmount && !hasDesc && !hasJob && !hasRate;
}

/**
 * SplitEntryPanel – shows split entry header info and sub-entries table.
 * Displayed as a callout when the user is viewing a split header or sub-entry.
 */
function SplitEntryPanel({
  headerEntry, subEntries, loadingSubEntries, editingSubEntryId,
  onEditSubEntry, onAddSubEntry, onSaveSubEntry, onDeleteSubEntry,
  onUnsplit, onCopySplit, copySplitLoading, rateCodes, customers, isHeaderView, topExpenseTypes,
}) {
  const headerTotal = parseFloat(headerEntry.TotalPrice) || 0;
  const allocatedTotal = subEntries.reduce((sum, s) => sum + (parseFloat(s.TotalPrice) || 0), 0);
  const remaining = headerTotal - allocatedTotal;
  const balanced = Math.abs(remaining) < 0.005;

  return (
    <div style={{
      margin: '8px 16px', padding: '12px', borderRadius: '8px',
      border: `2px solid ${balanced ? 'var(--primary, #2563eb)' : 'var(--warning, #f59e0b)'}`,
      background: balanced ? '#eff6ff' : '#fffbeb',
    }}>
      {/* Header info */}
      <div style={{ marginBottom: '10px' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: balanced ? '#1d4ed8' : '#92400e', marginBottom: '4px' }}>
          Split Entry
        </div>
        <div style={{ fontSize: '13px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <span><strong>Date:</strong> {headerEntry.EntryDate ? displayDate(headerEntry.EntryDate) : 'No date'}</span>
          <span><strong>Description:</strong> {headerEntry.Description || 'None'}</span>
          <span><strong>Total:</strong> {formatCurrency(headerEntry.TotalPrice)}</span>
        </div>
        {!balanced && (
          <div style={{ fontSize: '12px', color: '#92400e', fontWeight: 600, marginTop: '4px' }}>
            {remaining > 0 ? `Remaining: ${formatCurrency(remaining)}` : `Over-allocated by: ${formatCurrency(Math.abs(remaining))}`}
          </div>
        )}
      </div>

      {/* Sub-entries table */}
      <div style={{ fontSize: '12px' }}>
        <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '12px' }}>Sub-Entries:</div>
        {loadingSubEntries ? (
          <div>Loading...</div>
        ) : subEntries.length === 0 ? (
          <div style={{ color: 'var(--gray-500)' }}>No sub-entries yet.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--gray-300)' }}>
                <th style={{ textAlign: 'left', padding: '4px 6px' }}>Job/Task</th>
                <th style={{ textAlign: 'left', padding: '4px 6px' }}>Type</th>
                <th style={{ textAlign: 'left', padding: '4px 6px' }}>Description</th>
                <th style={{ textAlign: 'right', padding: '4px 6px' }}>Amount</th>
                <th style={{ padding: '4px 6px', width: '30px' }}></th>
              </tr>
            </thead>
            <tbody>
              {subEntries.map((sub) => {
                const isEditing = editingSubEntryId === sub.ExpenseEntryID;
                return isEditing ? (
                  <SubEntryEditRow
                    key={sub.ExpenseEntryID}
                    subEntry={sub}
                    rateCodes={rateCodes}
                    customers={customers}
                    headerEntry={headerEntry}
                    topExpenseTypes={topExpenseTypes}
                    onSave={(data) => onSaveSubEntry(sub.ExpenseEntryID, data)}
                    onDelete={() => onDeleteSubEntry(sub.ExpenseEntryID)}
                    onClose={() => onEditSubEntry(null)}
                  />
                ) : (
                  <tr
                    key={sub.ExpenseEntryID}
                    style={{ borderBottom: '1px solid var(--gray-100)', cursor: 'pointer' }}
                    onClick={() => onEditSubEntry(sub.ExpenseEntryID)}
                  >
                    <td style={{ padding: '6px' }}>
                      {sub.JobID ? `${sub.JobID}/${sub.JobTaskID || ''}` : <span style={{ color: 'var(--gray-400)' }}>-</span>}
                    </td>
                    <td style={{ padding: '6px' }}>{sub.RateCodeName || <span style={{ color: 'var(--gray-400)' }}>-</span>}</td>
                    <td style={{ padding: '6px' }}>
                      {sub.Description || <span style={{ color: 'var(--gray-400)' }}>-</span>}
                      {sub.NonBillable && <span style={{ fontSize: '10px', color: 'var(--gray-500)', marginLeft: '4px' }}>(NB)</span>}
                    </td>
                    <td style={{ padding: '6px', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(sub.TotalPrice)}</td>
                    <td style={{ padding: '6px' }}>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={(e) => { e.stopPropagation(); onDeleteSubEntry(sub.ExpenseEntryID); }}
                        style={{ fontSize: '10px', padding: '1px 4px', lineHeight: '1.2' }}
                      >&times;</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add sub-entry button + copy split + unsplit option */}
      <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
        <button className="btn btn-sm btn-primary" onClick={onAddSubEntry}>
          + Add Line
        </button>
        {onCopySplit && (
          <button className="btn btn-sm btn-outline" onClick={onCopySplit} disabled={copySplitLoading}>
            {copySplitLoading ? 'Loading...' : 'Copy Split'}
          </button>
        )}
        {subEntries.length === 1 && onUnsplit && (
          <button className="btn btn-sm btn-outline" onClick={onUnsplit}>
            Unsplit
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * SubEntryEditRow – inline editable row for a sub-entry within the split panel.
 * Saves onBlur for each field.
 */
function SubEntryEditRow({ subEntry, rateCodes, customers, headerEntry, onSave, onDelete, onClose, topExpenseTypes = [] }) {
  const [jobId, setJobId] = useState(subEntry.JobID || '');
  const [jobTaskId, setJobTaskId] = useState(subEntry.JobTaskID || '');
  const [rateSheetCode, setRateSheetCode] = useState(subEntry.RateSheetCode || '');
  const [rateCodeId, setRateCodeId] = useState(subEntry.RateCodeID || '');
  const [description, setDescription] = useState(subEntry.Description || '');
  const [totalPrice, setTotalPrice] = useState(subEntry.TotalPrice != null ? parseFloat(subEntry.TotalPrice).toFixed(2) : '');
  const [quantity, setQuantity] = useState(subEntry.Quantity != null ? String(subEntry.Quantity) : '1');
  const [unitPrice, setUnitPrice] = useState(subEntry.UnitPrice != null ? String(subEntry.UnitPrice) : '');
  const [nonBillable, setNonBillable] = useState(!!subEntry.NonBillable);
  const [tasks, setTasks] = useState([]);
  const [saving, setSaving] = useState(false);

  // Job picker: customer-filtered dropdown mode vs freeform search mode
  const [freeform, setFreeform] = useState(false);
  const [customerId, setCustomerId] = useState(subEntry.CustomerID || '');
  const [customerJobs, setCustomerJobs] = useState([]);
  const [loadingCustomerJobs, setLoadingCustomerJobs] = useState(false);
  // Freeform search state
  const [allJobs, setAllJobs] = useState([]);
  const [loadingAllJobs, setLoadingAllJobs] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFreeformJob, setSelectedFreeformJob] = useState(null);

  // Load customer jobs when customer changes (dropdown mode)
  useEffect(() => {
    if (freeform) return;
    if (!customerId) { setCustomerJobs([]); return; }
    setLoadingCustomerJobs(true);
    api.getJobs(customerId).then(setCustomerJobs).finally(() => setLoadingCustomerJobs(false));
  }, [customerId, freeform]);

  // Load all jobs when freeform is enabled
  useEffect(() => {
    if (!freeform || allJobs.length > 0) return;
    setLoadingAllJobs(true);
    api.getJobs().then(setAllJobs).finally(() => setLoadingAllJobs(false));
  }, [freeform, allJobs.length]);

  // Load tasks when jobId changes
  useEffect(() => {
    if (jobId) {
      api.getJobTasks(jobId, { taskCode: 'Expense' }).then(t => {
        setTasks(t);
        if (t.length === 1 && !jobTaskId) setJobTaskId(t[0].JobTaskID);
      }).catch(() => {});
    } else {
      setTasks([]);
    }
  }, [jobId]); // eslint-disable-line

  // Freeform: filtered jobs based on search query
  const filteredSearchJobs = useMemo(() => {
    if (!freeform) return [];
    const q = searchQuery.toLowerCase().trim();
    if (!q) return [];
    const custMap = {};
    for (const c of customers) {
      custMap[c.CustomerID] = c.CustomerName?.toLowerCase() || '';
    }
    return allJobs.filter((j) => {
      const custName = custMap[j.CustomerID] || '';
      return `${j.JobID} ${j.JobName} ${custName}`.toLowerCase().includes(q);
    }).slice(0, 30);
  }, [freeform, allJobs, searchQuery, customers]);

  const filteredRateCodes = rateCodes.filter(rc => rc.RateSheetCode === rateSheetCode);

  // Top 15 most-used expense types for this ratesheet
  const topFilteredExpenseTypes = useMemo(() => {
    if (!topExpenseTypes || topExpenseTypes.length === 0 || filteredRateCodes.length === 0) return [];
    const rankMap = new Map();
    topExpenseTypes.forEach((t, i) => {
      rankMap.set(`${t.RateSheetCode}|${t.RateCodeID}`, i);
    });
    return filteredRateCodes
      .filter(rc => rankMap.has(`${rc.RateSheetCode}|${rc.RateCodeID}`))
      .sort((a, b) => {
        const aRank = rankMap.get(`${a.RateSheetCode}|${a.RateCodeID}`);
        const bRank = rankMap.get(`${b.RateSheetCode}|${b.RateCodeID}`);
        return aRank - bRank;
      });
  }, [filteredRateCodes, topExpenseTypes]);

  const doSave = async (overrides = {}) => {
    setSaving(true);
    try {
      await onSave({
        entryDate: subEntry.EntryDate ? subEntry.EntryDate.split('T')[0] : null,
        jobId: overrides.jobId !== undefined ? overrides.jobId : (jobId || null),
        jobTaskId: overrides.jobTaskId !== undefined ? overrides.jobTaskId : (jobTaskId || null),
        rateSheetCode: overrides.rateSheetCode !== undefined ? overrides.rateSheetCode : (rateSheetCode || null),
        rateCodeId: overrides.rateCodeId !== undefined ? overrides.rateCodeId : (rateCodeId || null),
        description: overrides.description !== undefined ? overrides.description : description,
        quantity: parseFloat(overrides.quantity !== undefined ? overrides.quantity : quantity) || null,
        unitPrice: parseFloat(overrides.unitPrice !== undefined ? overrides.unitPrice : unitPrice) || null,
        totalPrice: parseFloat(overrides.totalPrice !== undefined ? overrides.totalPrice : totalPrice) || null,
        nonBillable: overrides.nonBillable !== undefined ? overrides.nonBillable : nonBillable,
        nonReimbursable: subEntry.NonReimbursable,
      });
    } catch (e) {
      console.error('Failed to save sub-entry:', e);
    }
    setSaving(false);
  };

  const handleJobSelect = (job) => {
    setJobId(job.JobID);
    setJobTaskId('');
    const sheet = job.RateSheetCode || '';
    setRateSheetCode(sheet);
    setRateCodeId('');
  };

  const handleDropdownJobChange = (newJobId) => {
    setJobId(newJobId);
    setJobTaskId('');
    const job = customerJobs.find(j => j.JobID === newJobId);
    const sheet = job?.RateSheetCode || '';
    setRateSheetCode(sheet);
    setRateCodeId('');
  };

  const handleTaskChange = (newTaskId) => {
    setJobTaskId(newTaskId);
    doSave({ jobTaskId: newTaskId });
  };

  const handleRateCodeChange = (value) => {
    const [sheet, code] = value.split('|');
    setRateSheetCode(sheet || '');
    setRateCodeId(code || '');
    const rc = rateCodes.find(r => r.RateCodeID === code && r.RateSheetCode === sheet);
    const rate = rc ? parseFloat(rc.Rate) : 0;
    if (rate !== 0) {
      setUnitPrice(String(rate));
      const qty = parseFloat(quantity) || 0;
      const newTotal = String((qty * rate).toFixed(2));
      setTotalPrice(newTotal);
      doSave({ rateSheetCode: sheet, rateCodeId: code, unitPrice: rate, totalPrice: newTotal });
    } else {
      doSave({ rateSheetCode: sheet, rateCodeId: code });
    }
  };

  return (
    <tr style={{ background: '#f0f9ff', borderBottom: '2px solid var(--primary, #2563eb)' }}>
      <td colSpan={5} style={{ padding: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--primary)' }}>Editing Sub-Entry</span>
          <button className="btn btn-sm btn-outline" onClick={onClose} style={{ fontSize: '10px', padding: '1px 6px' }}>Done</button>
        </div>

        {/* Job / Task picker with customer filter and search */}
        <div style={{ marginBottom: '6px', padding: '6px', background: 'white', borderRadius: '4px', border: '1px solid var(--gray-100)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <label style={{ fontSize: '11px', fontWeight: 600, margin: 0 }}>Job / Task</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '11px', cursor: 'pointer', margin: 0 }}>
              <input
                type="checkbox"
                checked={freeform}
                onChange={(e) => {
                  setFreeform(e.target.checked);
                  setSelectedFreeformJob(null);
                  setSearchQuery('');
                }}
                style={{ width: '12px', height: '12px' }}
              />
              Search
            </label>
          </div>

          {!freeform ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', fontSize: '12px' }}>
              <div>
                <label style={{ fontSize: '10px', color: 'var(--gray-500)' }}>Customer</label>
                <select
                  className="form-control"
                  value={customerId}
                  onChange={(e) => { setCustomerId(e.target.value); setJobId(''); setJobTaskId(''); }}
                  style={{ fontSize: '12px', padding: '4px' }}
                >
                  <option value="">Select customer...</option>
                  {customers.map(c => <option key={c.CustomerID} value={c.CustomerID}>{c.CustomerName}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '10px', color: 'var(--gray-500)' }}>Job</label>
                <select
                  className="form-control"
                  value={jobId}
                  onChange={(e) => handleDropdownJobChange(e.target.value)}
                  disabled={!customerId || loadingCustomerJobs}
                  style={{ fontSize: '12px', padding: '4px' }}
                >
                  <option value="">{loadingCustomerJobs ? 'Loading...' : 'Select job...'}</option>
                  {customerJobs.map(j => <option key={j.JobID} value={j.JobID}>{j.JobID} - {j.JobName}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: '10px', color: 'var(--gray-500)' }}>Task</label>
                {tasks.length === 1 ? (
                  <div className="form-control" style={{ background: 'white', fontSize: '12px', padding: '4px' }}>
                    {tasks[0].JobTaskID} - {tasks[0].TaskName}
                  </div>
                ) : (
                  <select className="form-control" value={jobTaskId} onChange={(e) => handleTaskChange(e.target.value)} disabled={!jobId} style={{ fontSize: '12px', padding: '4px' }}>
                    <option value="">Select task...</option>
                    {tasks.map(t => <option key={t.JobTaskID} value={t.JobTaskID}>{t.JobTaskID} - {t.TaskName}</option>)}
                  </select>
                )}
              </div>
            </div>
          ) : (
            <div style={{ fontSize: '12px' }}>
              <div style={{ marginBottom: '4px' }}>
                <label style={{ fontSize: '10px', color: 'var(--gray-500)' }}>Search by job, customer, or ID</label>
                <input
                  className="form-control"
                  type="text"
                  placeholder={loadingAllJobs ? 'Loading jobs...' : 'Type to search...'}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSelectedFreeformJob(null);
                    setJobId('');
                    setJobTaskId('');
                  }}
                  disabled={loadingAllJobs}
                  style={{ fontSize: '12px', padding: '4px' }}
                />
                {filteredSearchJobs.length > 0 && !selectedFreeformJob && (
                  <div style={{
                    maxHeight: '120px', overflowY: 'auto', border: '1px solid var(--gray-200)',
                    borderRadius: '4px', marginTop: '2px', background: 'white', position: 'relative', zIndex: 10,
                  }}>
                    {filteredSearchJobs.map((j) => {
                      const custName = customers.find(c => c.CustomerID === j.CustomerID)?.CustomerName || '';
                      return (
                        <div
                          key={j.JobID}
                          onClick={() => {
                            setSelectedFreeformJob(j);
                            setSearchQuery(`${j.JobID} - ${j.JobName}`);
                            handleJobSelect(j);
                          }}
                          style={{
                            padding: '4px 8px', cursor: 'pointer', fontSize: '12px',
                            borderBottom: '1px solid var(--gray-100)',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--gray-50)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                        >
                          <div style={{ fontWeight: 500 }}>{j.JobID} - {j.JobName}</div>
                          {custName && <div style={{ fontSize: '10px', color: 'var(--gray-500)' }}>{custName}</div>}
                        </div>
                      );
                    })}
                  </div>
                )}
                {selectedFreeformJob && (
                  <div style={{ fontSize: '11px', color: 'var(--gray-500)', marginTop: '2px' }}>
                    Selected: {selectedFreeformJob.JobID} - {selectedFreeformJob.JobName}
                    <button
                      onClick={() => {
                        setSelectedFreeformJob(null);
                        setSearchQuery('');
                        setJobId('');
                        setJobTaskId('');
                      }}
                      style={{ marginLeft: '6px', background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '11px' }}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label style={{ fontSize: '10px', color: 'var(--gray-500)' }}>Task</label>
                {tasks.length === 1 ? (
                  <div className="form-control" style={{ background: 'white', fontSize: '12px', padding: '4px' }}>
                    {tasks[0].JobTaskID} - {tasks[0].TaskName}
                  </div>
                ) : (
                  <select className="form-control" value={jobTaskId} onChange={(e) => handleTaskChange(e.target.value)} disabled={!jobId} style={{ fontSize: '12px', padding: '4px' }}>
                    <option value="">Select task...</option>
                    {tasks.map(t => <option key={t.JobTaskID} value={t.JobTaskID}>{t.JobTaskID} - {t.TaskName}</option>)}
                  </select>
                )}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '12px' }}>
          <div>
            <label style={{ fontSize: '11px', fontWeight: 500 }}>Type</label>
            <select
              className="form-control"
              value={rateSheetCode && rateCodeId ? `${rateSheetCode}|${rateCodeId}` : ''}
              onChange={(e) => handleRateCodeChange(e.target.value)}
              style={{ fontSize: '12px', padding: '4px' }}
            >
              <option value="">{!jobId ? 'Select job first...' : 'Select type...'}</option>
              {topFilteredExpenseTypes.length > 0 && (
                <optgroup label="Most Used">
                  {topFilteredExpenseTypes.map((rc) => (
                    <option key={`top-${rc.RateSheetCode}|${rc.RateCodeID}`} value={`${rc.RateSheetCode}|${rc.RateCodeID}`}>
                      {rc.RateCodeName}{rc.Rate > 0 ? ` ($${parseFloat(rc.Rate).toFixed(2)}/unit)` : ''}
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label={topFilteredExpenseTypes.length > 0 ? 'All Expense Types' : undefined}>
                {filteredRateCodes.map((rc) => (
                  <option key={`all-${rc.RateSheetCode}|${rc.RateCodeID}`} value={`${rc.RateSheetCode}|${rc.RateCodeID}`}>
                    {rc.RateCodeName}{rc.Rate > 0 ? ` ($${parseFloat(rc.Rate).toFixed(2)}/unit)` : ''}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '11px', fontWeight: 500 }}>Amount</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '6px', top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)', fontSize: '12px', pointerEvents: 'none' }}>$</span>
              <input
                type="text"
                className="form-control no-spinners"
                value={totalPrice}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9.\-]/g, '');
                  setTotalPrice(val);
                }}
                onBlur={() => {
                  const num = parseFloat(totalPrice);
                  const formatted = !isNaN(num) ? num.toFixed(2) : totalPrice;
                  setTotalPrice(formatted);
                  doSave({ totalPrice: formatted, unitPrice: formatted, quantity: '1' });
                }}
                inputMode="decimal"
                style={{ fontSize: '12px', padding: '4px', paddingLeft: '16px' }}
              />
            </div>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ fontSize: '11px', fontWeight: 500 }}>Description</label>
            <input
              type="text"
              className="form-control"
              value={description}
              maxLength={250}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={() => doSave({ description })}
              placeholder="Describe..."
              style={{ fontSize: '12px', padding: '4px' }}
            />
          </div>
        </div>

        <div style={{ marginTop: '6px', display: 'flex', gap: '6px', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <input
              type="checkbox"
              id={`sub-nb-${subEntry.ExpenseEntryID}`}
              checked={nonBillable}
              onChange={(e) => {
                setNonBillable(e.target.checked);
                doSave({ nonBillable: e.target.checked });
              }}
              style={{ width: '14px', height: '14px' }}
            />
            <label htmlFor={`sub-nb-${subEntry.ExpenseEntryID}`} style={{ margin: 0, fontSize: '11px' }}>
              Non-Billable
            </label>
          </div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            {saving && <span style={{ fontSize: '11px', color: 'var(--gray-500)' }}>Saving...</span>}
            <button className="btn btn-sm btn-danger" onClick={onDelete} style={{ fontSize: '10px', padding: '2px 6px' }}>Delete</button>
          </div>
        </div>
      </td>
    </tr>
  );
}

/**
 * EntryReceiptSection – shows receipt status, thumbnail, upload for a specific entry.
 */
function EntryReceiptSection({ entryId, receipts, onUpload, onDelete, uploading, onPreview, onEditImage, imageVersion = 0 }) {
  const entryReceipts = receipts.filter(r => r.ExpenseEntryID === entryId);
  const fileInputRef = React.useRef(null);
  const [entryDragOver, setEntryDragOver] = React.useState(false);

  React.useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      // If the clipboard contains any text (e.g. an Excel cell copy carries both
      // text and an image rendering), paste it as text and do not treat it as a
      // receipt. Only genuine image/file copies (no text) are uploaded.
      const hasText = Array.from(items).some(
        (it) => it.kind === 'string' && it.type.startsWith('text/')
      );
      if (hasText) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const blob = item.getAsFile();
          if (blob) {
            const ext = item.type.split('/')[1] || 'png';
            const file = new File([blob], `pasted-receipt-${Date.now()}.${ext}`, { type: item.type });
            onUpload(file);
          }
          return;
        }
      }
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [onUpload]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
    e.target.value = '';
  };

  return (
    <div
      style={{
        margin: '0 16px 8px', padding: '10px 12px', borderRadius: '8px',
        border: entryDragOver ? '2px dashed var(--primary)' : !isMobile ? '2px dashed #93c5fd' : '1px solid var(--gray-200)',
        background: entryDragOver ? 'var(--primary-light)' : 'var(--gray-50)',
      }}
      {...(!isMobile ? {
        onDragOver: (e) => { e.preventDefault(); setEntryDragOver(true); },
        onDragLeave: () => setEntryDragOver(false),
        onDrop: (e) => { e.preventDefault(); setEntryDragOver(false); const files = Array.from(e.dataTransfer.files); if (files.length > 0) onUpload(files[0]); },
      } : {})}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: entryReceipts.length > 0 ? '8px' : '0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600 }}>
          {entryDragOver ? (
            <span style={{ color: 'var(--primary)' }}>Drop file to upload</span>
          ) : entryReceipts.length > 0 ? (
            <>
              <span style={{ color: '#16a34a', fontSize: '14px' }}>&#x2713;</span>
              <span style={{ color: '#16a34a' }}>Receipt Attached</span>
            </>
          ) : (
            <span style={{ color: 'var(--gray-500)' }}>No Receipt{!isMobile ? ' — drag file here or click button to upload' : ''}</span>
          )}
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf,.eml,.msg"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <button
            className="btn btn-outline btn-sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            style={{ fontSize: '12px', padding: '3px 10px' }}
          >
            {uploading ? 'Uploading...' : 'Upload Receipt'}
          </button>
        </div>
      </div>
      {entryReceipts.map((r) => {
        const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(r.ReceiptPath);
        const previewUrl = `/api/expense-receipts/${r.ExpenseReceiptID}/preview?v=${imageVersion}`;
        return (
          <div key={r.ExpenseReceiptID} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div
              style={{ flexShrink: 0, width: '50px', height: '50px', cursor: 'pointer', borderRadius: '4px', overflow: 'hidden', border: '1px solid var(--gray-200)', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={() => onPreview(previewUrl, !isImage, r.ExpenseReceiptID)}
            >
              {isImage ? (
                <img src={previewUrl} alt="Receipt" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <span style={{ fontSize: '11px', color: 'var(--gray-500)' }}>PDF</span>
              )}
            </div>
            <div style={{ flex: 1, fontSize: '12px', color: 'var(--gray-600)' }}>
              {r.ReceiptPath.split('/').pop()}
            </div>
            {isImage && onEditImage && (
              <button
                className="btn btn-sm btn-outline"
                onClick={() => onEditImage(r.ExpenseReceiptID)}
                title="Rotate or crop image"
                style={{ fontSize: '10px', padding: '2px 6px' }}
              >&#9998; Edit</button>
            )}
            <button
              className="btn btn-sm btn-danger"
              onClick={() => onDelete(r.ExpenseReceiptID)}
              style={{ fontSize: '10px', padding: '2px 6px' }}
            >&times;</button>
          </div>
        );
      })}
    </div>
  );
}

// Expenses tabs and their addresses (/expenses = Summary, /expenses/<slug>).
const EXPENSE_TAB_SLUGS = { entries: 'entry', receipts: 'receipts', reports: 'reports' };

export default function ExpensesPage() {
  const { debugMode } = useDebug();
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;
  const { tab: tabSlug } = useParams();
  const [activeTab, setActiveTab] = useState(() => tabForSlug(EXPENSE_TAB_SLUGS, tabSlug, 'submission'));
  const [entries, setEntries] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [rateCodes, setRateCodes] = useState([]);
  const [topExpenseTypes, setTopExpenseTypes] = useState([]);
  const [recentJobs, setRecentJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Credit card import state
  const [creditCardDebugLogs, setCreditCardDebugLogs] = useState([]);

  // Receipts state
  const [receipts, setReceipts] = useState([]);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [assignReceiptId, setAssignReceiptId] = useState(null);
  const [assignSelectedEntries, setAssignSelectedEntries] = useState(new Set());
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState(null);
  const [receiptPreviewIsPdf, setReceiptPreviewIsPdf] = useState(false);
  const [receiptPreviewId, setReceiptPreviewId] = useState(null);
  const [receiptDebugLogs, setReceiptDebugLogs] = useState([]);
  // Image editor (rotate/crop) state; imageVersion busts cached preview images after an edit
  const [editorReceiptId, setEditorReceiptId] = useState(null);
  const [imageVersion, setImageVersion] = useState(0);
  const [hideMatchedReceipts, setHideMatchedReceipts] = useState(true);
  const [autoMatchResults, setAutoMatchResults] = useState(null);
  const [autoMatchSelected, setAutoMatchSelected] = useState(new Set());
  const [autoMatching, setAutoMatching] = useState(false);
  const [applyingMatches, setApplyingMatches] = useState(false);

  // Submission state. The unsubmitted list is DERIVED from the single `entries`
  // source (see below) rather than fetched separately, so the Summary tab can't
  // fall out of sync with the Entry tab.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [sortBy, setSortBy] = useState('date');
  const [loadingSubmission, setLoadingSubmission] = useState(false);

  // Multi-assign mode state
  const [multiAssignMode, setMultiAssignMode] = useState(false);
  const [maSelectedIds, setMaSelectedIds] = useState(new Set());
  const [maJobId, setMaJobId] = useState('');
  const [maJobTaskId, setMaJobTaskId] = useState('');
  const [maRateCodeValue, setMaRateCodeValue] = useState('');
  const [maCustomerId, setMaCustomerId] = useState('');
  const [maJobs, setMaJobs] = useState([]);
  const [maTasks, setMaTasks] = useState([]);
  const [maLoadingJobs, setMaLoadingJobs] = useState(false);
  const [maLoadingTasks, setMaLoadingTasks] = useState(false);
  const [maFreeform, setMaFreeform] = useState(false);
  const [maAllJobs, setMaAllJobs] = useState([]);
  const [maLoadingAllJobs, setMaLoadingAllJobs] = useState(false);
  const [maSearchQuery, setMaSearchQuery] = useState('');
  const [maSelectedFreeformJob, setMaSelectedFreeformJob] = useState(null);
  const [maApplying, setMaApplying] = useState(false);

  // Reports state
  const [reports, setReports] = useState([]);
  const [reportsTotal, setReportsTotal] = useState(0);
  const [reportsPage, setReportsPage] = useState(1);
  const [loadingReports, setLoadingReports] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loadingReportDetail, setLoadingReportDetail] = useState(false);
  // Add-receipt-to-report state
  const [addReceiptEntryId, setAddReceiptEntryId] = useState(null);
  const [attachingReceipt, setAttachingReceipt] = useState(false);
  // Receipt IDs currently being removed (for optimistic "Removing Receipt..." UI)
  const [removingReceiptIds, setRemovingReceiptIds] = useState(new Set());

  // Combined/custom report state
  const [checkedReportIds, setCheckedReportIds] = useState(new Set());
  const [showCustomReport, setShowCustomReport] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [customJobId, setCustomJobId] = useState('');
  const [reportJobs, setReportJobs] = useState([]);

  // Admin custom report state
  const [showAdminReport, setShowAdminReport] = useState(false);
  const [adminStartDate, setAdminStartDate] = useState('');
  const [adminEndDate, setAdminEndDate] = useState('');
  const [adminJobId, setAdminJobId] = useState('');
  const [adminUserId, setAdminUserId] = useState('');
  const [adminReportUsers, setAdminReportUsers] = useState([]);
  const [adminReportJobs, setAdminReportJobs] = useState([]);

  // Track current entry for auto-delete on navigate away
  const currentEntryRef = useRef(null);
  // Ref to ExpenseEntryCard for auto-save
  const entryCardRef = useRef(null);

  // Sync debug logs state
  const [syncDebugLogs, setSyncDebugLogs] = useState([]);

  // PDF generation state
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  const openPdf = useCallback(async (url) => {
    setPdfError(null);
    setPdfLoading(true);
    try {
      const resp = await fetch(url, { credentials: 'same-origin' });
      if (resp.status === 401) {
        window.location.href = '/auth/login';
        return;
      }
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || `Request failed: ${resp.status}`);
      }
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (e) {
      setPdfError(e.message);
    } finally {
      setPdfLoading(false);
    }
  }, []);

  // Entry operation debug logs state
  const [entryDebugLogs, setEntryDebugLogs] = useState([]);

  // Load data and import credit card transactions
  useEffect(() => {
    async function init() {
      try {
        // Import credit card transactions first, then load entries
        try {
          const importResult = await api.importCreditCardTransactions(debugMode);
          if (debugMode && importResult?._debug) {
            console.log('[Credit Card Import Debug]', importResult._debug);
            setCreditCardDebugLogs(importResult._debug);
          }
          if (importResult?.imported > 0) {
            console.log(`Imported ${importResult.imported} credit card transaction(s)`);
          }
        } catch (importErr) {
          console.error('Error importing credit card transactions:', importErr);
          if (debugMode) {
            setCreditCardDebugLogs([{ step: 'import_error', error: importErr.message }]);
          }
        }

        // Sync expense report statuses from NAV on login
        try {
          const syncResult = await api.syncExpenseReportStatuses(debugMode);
          if (debugMode && syncResult?._debug) {
            console.log('[Expense Status Sync Debug]', syncResult._debug);
            setSyncDebugLogs(syncResult._debug);
          }
        } catch (syncErr) {
          console.error('Error syncing expense report statuses:', syncErr);
          if (debugMode) {
            setSyncDebugLogs([{ step: 'sync_request_failed', error: syncErr.message }, ...(syncErr.responseBody?._debug || [])]);
          }
        }

        const [custs, rcs, exps, topTypes, recent] = await Promise.all([
          api.getCustomers(),
          api.getRateCodes(null, 'Expense'),
          api.getExpenseEntries(),
          api.getTopExpenseTypes().catch(() => []),
          api.getRecentJobs().catch(() => []),
        ]);
        setCustomers(custs);
        setRateCodes(rcs);
        setEntries(exps);
        setTopExpenseTypes(topTypes);
        setRecentJobs(recent);
      } catch (e) {
        console.error('Error loading expense data:', e);
      }
      setLoading(false);
    }
    init();
  }, []); // eslint-disable-line

  const refreshEntries = useCallback(async () => {
    try {
      const exps = await api.getExpenseEntries();
      setEntries(exps);
    } catch (e) { console.error(e); }
  }, []);

  const refreshUnsubmitted = useCallback(async () => {
    // The Summary tab now derives from `entries`; refreshing that single source
    // updates it. Keep the loadingSubmission flag for the tab's spinner.
    setLoadingSubmission(true);
    try {
      await refreshEntries();
    } catch (e) { console.error(e); }
    setLoadingSubmission(false);
  }, [refreshEntries]);

  const refreshReceipts = useCallback(async () => {
    setLoadingReceipts(true);
    try {
      const r = await api.getExpenseReceipts();
      setReceipts(r);
    } catch (e) { console.error(e); }
    setLoadingReceipts(false);
  }, []);

  const refreshReports = useCallback(async (page = 1, doSync = false) => {
    setLoadingReports(true);
    try {
      // Sync statuses from NAV when entering Reports tab
      if (doSync) {
        try {
          const syncResult = await api.syncExpenseReportStatuses(debugMode);
          if (debugMode && syncResult?._debug) {
            console.log('[Expense Status Sync Debug]', syncResult._debug);
            setSyncDebugLogs(syncResult._debug);
          }
        } catch (syncErr) {
          console.error('Error syncing expense report statuses:', syncErr);
          if (debugMode) {
            setSyncDebugLogs([{ step: 'sync_request_failed', error: syncErr.message }, ...(syncErr.responseBody?._debug || [])]);
          }
        }
      }
      const data = await api.getExpenseReports(page);
      setReports(data.reports);
      setReportsTotal(data.total);
      setReportsPage(page);
    } catch (e) { console.error(e); }
    setLoadingReports(false);
  }, [debugMode]);

  // Auto-delete empty entries when navigating away from Entry tab or switching entries
  const autoDeleteEmptyEntry = useCallback(async (entry) => {
    if (entry && isEntryEmpty(entry)) {
      try {
        await api.deleteExpenseEntry(entry.ExpenseEntryID, debugMode);
        await refreshEntries();
      } catch (e) { /* ignore */ }
    }
  }, [refreshEntries]);

  // Split entry state
  const [subEntries, setSubEntries] = useState([]);
  const [loadingSubEntries, setLoadingSubEntries] = useState(false);
  const [editingSubEntryId, setEditingSubEntryId] = useState(null);
  const [copySplitLoading, setCopySplitLoading] = useState(false);
  const [copySplitOptions, setCopySplitOptions] = useState(null);

  // Track current entry ref for cleanup
  // Filter to only header entries and standalone entries (not sub-entries) for navigation
  const editableEntries = entries.filter(e => !e.ExpenseReportID && !e.HeaderEntryID);
  const currentEntry = editableEntries[currentIndex] || null;

  // Unsubmitted entries (top-level + their sub-entries), DERIVED from the single
  // `entries` source: everything not yet attached to an expense report
  // (ExpenseReportID IS NULL). The Summary tab reads this, so it can never race
  // or disagree with the Entry tab's `entries`.
  const unsubmittedEntries = entries.filter(e => !e.ExpenseReportID);

  // Navigation order matches the Summary tab's default order so the Up/Down
  // (prev/next) buttons line up with the summary list: the first summary item
  // only offers Down, the last only Up. `editableEntries` stays in its original
  // (indexing) order; `navSequence` is only the traversal order.
  const navSequence = [...editableEntries].sort((a, b) => {
    if (sortBy === 'date') return (a.EntryDate || '').localeCompare(b.EntryDate || '');
    const aKey = `${a.JobID || ''}|${a.JobTaskID || ''}`;
    const bKey = `${b.JobID || ''}|${b.JobTaskID || ''}`;
    return aKey.localeCompare(bKey);
  });
  const navPos = currentEntry ? navSequence.findIndex(e => e.ExpenseEntryID === currentEntry.ExpenseEntryID) : -1;
  const entryNavLabel = (e) => {
    if (!e) return '';
    const raw = (e.Description || '').replace(/^Credit Card Charge by:\s*/i, '');
    const desc = raw ? raw.substring(0, 30) : 'No desc';
    const amt = e.TotalPrice != null ? `$${parseFloat(e.TotalPrice).toFixed(2)}` : '$0.00';
    return `${desc} (${amt})`;
  };
  // Move to the prev/next entry in summary order (delta = -1 up, +1 down). For a
  // split (header) expense this lands on the adjacent header/standalone expense,
  // never on the current expense's sub-entries (those aren't in editableEntries).
  const goNavEntry = (delta) => {
    const target = navSequence[navPos + delta];
    if (!target) return;
    const idx = editableEntries.findIndex(e => e.ExpenseEntryID === target.ExpenseEntryID);
    if (idx >= 0) handleNavEntry(idx);
  };

  // Determine if current entry is a split group header
  const isHeaderEntry = currentEntry &&
    entries.some(e => e.HeaderEntryID === currentEntry.ExpenseEntryID);
  const isSplitContext = isHeaderEntry;
  const headerEntryId = isHeaderEntry ? currentEntry.ExpenseEntryID : null;
  const headerEntry = headerEntryId ? currentEntry : null;

  useEffect(() => {
    currentEntryRef.current = currentEntry;
  }, [currentEntry]);

  // Track whether the entries tab was entered via direct click (vs. programmatic navigation to an existing entry)
  const entryTabDirectRef = useRef(false);

  // Load tab-specific data on tab switch, auto-save then auto-delete empty entry when leaving entries tab
  const handleTabSwitch = useCallback(async (newTab) => {
    if (activeTab === 'entries' && newTab !== 'entries') {
      // Auto-save any unsaved changes before leaving entries tab
      if (entryCardRef.current?.saveIfDirty) {
        await entryCardRef.current.saveIfDirty();
      }
      await autoDeleteEmptyEntry(currentEntryRef.current);
    }
    if (newTab === 'entries') {
      entryTabDirectRef.current = true;
    }
    setActiveTab(newTab);
  }, [activeTab, autoDeleteEmptyEntry])
  useTabInPath({ base: '/expenses', slugs: EXPENSE_TAB_SLUGS, tab: activeTab, defaultTab: 'submission', onUrlTab: handleTabSwitch });

  // Track whether we've already done the one-time auto-refresh for empty summary
  const summaryAutoRefreshedRef = useRef(false);

  useEffect(() => {
    if (activeTab === 'receipts') refreshReceipts();
    if (activeTab === 'entries') refreshReceipts();
    if (activeTab === 'submission') { refreshUnsubmitted(); refreshReceipts(); setMultiAssignMode(false); }
    if (activeTab === 'reports') refreshReports(1, true);
  }, [activeTab, refreshReceipts, refreshUnsubmitted, refreshReports]);

  // Auto-create a new expense entry when the user navigates to the Entries tab directly
  useEffect(() => {
    if (activeTab !== 'entries' || !entryTabDirectRef.current || loading) return;
    entryTabDirectRef.current = false;
    (async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        const result = await api.createExpenseEntry({ entryDate: today }, debugMode);
        const updated = await api.getExpenseEntries();
        const newIdx = updated.filter(e => !e.ExpenseReportID && !e.HeaderEntryID).findIndex(e => e.ExpenseEntryID === result.ExpenseEntryID);
        setEntries(updated);
        setCurrentIndex(newIdx >= 0 ? newIdx : 0);
      } catch (_) { /* ignore – user can create manually */ }
    })();
  }, [activeTab, loading, debugMode]);

  // Auto-refresh expense summary once after 5 seconds if no expenses loaded from NAV
  useEffect(() => {
    if (activeTab !== 'submission' || loadingSubmission || summaryAutoRefreshedRef.current) return;
    if (unsubmittedEntries.length > 0) return;
    const timer = setTimeout(async () => {
      summaryAutoRefreshedRef.current = true;
      await refreshUnsubmitted();
    }, 5000);
    return () => clearTimeout(timer);
  }, [activeTab, loadingSubmission, unsubmittedEntries.length, refreshUnsubmitted]);

  // ============================================================
  // Entries tab handlers
  // ============================================================
  const handleNewExpense = async () => {
    // Auto-save any unsaved changes before creating a new entry
    if (entryCardRef.current?.saveIfDirty) {
      await entryCardRef.current.saveIfDirty();
    }
    // Auto-delete current empty entry before creating a new one
    await autoDeleteEmptyEntry(currentEntryRef.current);
    try {
      const today = new Date().toISOString().split('T')[0];
      const result = await api.createExpenseEntry({ entryDate: today }, debugMode);
      if (debugMode && result?._debug) {
        console.log('[Expense Create Debug]', result._debug);
        setEntryDebugLogs(result._debug);
      }
      // Navigate to the new entry
      const updated = await api.getExpenseEntries();
      const newIdx = updated.filter(e => !e.ExpenseReportID && !e.HeaderEntryID).findIndex(e => e.ExpenseEntryID === result.ExpenseEntryID);
      setEntries(updated);
      setCurrentIndex(newIdx >= 0 ? newIdx : 0);
    } catch (e) {
      if (debugMode && e.responseBody?._debug) {
        console.log('[Expense Create Debug - Error]', e.responseBody._debug);
        setEntryDebugLogs(e.responseBody._debug);
      }
      alert(e.message || 'Failed to create expense');
    }
  };

  const handleSaveEntry = useCallback(async (data) => {
    if (!currentEntry) return;
    const updated = await api.updateExpenseEntry(currentEntry.ExpenseEntryID, data, debugMode);
    if (debugMode && updated?._debug) {
      console.log('[Expense Update Debug]', updated._debug);
      setEntryDebugLogs(updated._debug);
    }
    if (updated && updated.ExpenseEntryID) {
      setEntries(prev => prev.map(e =>
        e.ExpenseEntryID === updated.ExpenseEntryID ? updated : e
      ));
    }
  }, [currentEntry, debugMode]);

  const handleDeleteEntry = useCallback(async () => {
    if (!currentEntry) return;
    const result = await api.deleteExpenseEntry(currentEntry.ExpenseEntryID, debugMode);
    if (debugMode && result?._debug) {
      console.log('[Expense Delete Debug]', result._debug);
      setEntryDebugLogs(result._debug);
    }
    await refreshEntries();
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  }, [currentEntry, currentIndex, refreshEntries, debugMode]);

  // Load sub-entries when viewing a split context
  const loadSubEntries = useCallback(async (headId) => {
    if (!headId) { setSubEntries([]); return; }
    setLoadingSubEntries(true);
    try {
      const subs = await api.getSubEntries(headId);
      setSubEntries(subs);
    } catch (e) { console.error(e); setSubEntries([]); }
    setLoadingSubEntries(false);
  }, []);

  useEffect(() => {
    if (headerEntryId) {
      loadSubEntries(headerEntryId);
    } else {
      setSubEntries([]);
      setEditingSubEntryId(null);
    }
  }, [headerEntryId, loadSubEntries]);

  const handleSplitEntry = async () => {
    if (!currentEntry) return;
    try {
      const entryId = currentEntry.ExpenseEntryID;
      const result = await api.splitExpenseEntry(entryId);
      const updated = await api.getExpenseEntries();
      setEntries(updated);
      // Ensure we stay on the header entry (find its new index in editable list)
      const editable = updated.filter(e => !e.ExpenseReportID && !e.HeaderEntryID);
      const headerIdx = editable.findIndex(e => e.ExpenseEntryID === entryId);
      if (headerIdx >= 0) setCurrentIndex(headerIdx);
      await loadSubEntries(entryId);
      // Auto-open the first sub-entry for editing
      if (result?.subEntry?.ExpenseEntryID) {
        setEditingSubEntryId(result.subEntry.ExpenseEntryID);
      }
    } catch (e) {
      alert(e.message || 'Failed to split entry');
    }
  };

  const handleAddSubEntry = async () => {
    if (!headerEntryId) return;
    try {
      const savedHeaderId = headerEntryId;
      const result = await api.addSubEntry(savedHeaderId);
      const updated = await api.getExpenseEntries();
      setEntries(updated);
      // Ensure we stay on the header entry (its index may shift after new sub-entry is added)
      const editable = updated.filter(e => !e.ExpenseReportID && !e.HeaderEntryID);
      const headerIdx = editable.findIndex(e => e.ExpenseEntryID === savedHeaderId);
      if (headerIdx >= 0) setCurrentIndex(headerIdx);
      await loadSubEntries(savedHeaderId);
      // Start editing the new sub-entry
      if (result?.subEntry?.ExpenseEntryID) {
        setEditingSubEntryId(result.subEntry.ExpenseEntryID);
      }
    } catch (e) {
      alert(e.message || 'Failed to add sub-entry');
    }
  };

  const handleSaveSubEntry = useCallback(async (subEntryId, data) => {
    await api.updateExpenseEntry(subEntryId, data, debugMode);
    // Optimistically update the edited sub-entry in local state to prevent
    // the bottom row from briefly showing $0.00 during the async refresh.
    setSubEntries(prev => prev.map(s =>
      s.ExpenseEntryID === subEntryId
        ? { ...s, TotalPrice: data.totalPrice, Description: data.description, JobID: data.jobId, JobTaskID: data.jobTaskId, RateSheetCode: data.rateSheetCode, RateCodeID: data.rateCodeId }
        : s
    ));
    // Refresh entries and sub-entries in parallel; skip the loading indicator
    // so the table stays visible during the refresh.
    const subPromise = (async () => {
      try {
        const subs = await api.getSubEntries(headerEntryId);
        setSubEntries(subs);
      } catch (e) { console.error(e); }
    })();
    await Promise.all([refreshEntries(), subPromise]);
  }, [debugMode, refreshEntries, headerEntryId]);

  const handleDeleteSubEntry = useCallback(async (subEntryId) => {
    if (!window.confirm('Delete this sub-entry?')) return;
    const savedHeaderId = headerEntryId;
    await api.deleteExpenseEntry(subEntryId, debugMode);
    setEditingSubEntryId(null);
    const updated = await api.getExpenseEntries();
    setEntries(updated);
    // Stay on the header entry after deletion
    if (savedHeaderId) {
      const editable = updated.filter(e => !e.ExpenseReportID && !e.HeaderEntryID);
      const headerIdx = editable.findIndex(e => e.ExpenseEntryID === savedHeaderId);
      if (headerIdx >= 0) setCurrentIndex(headerIdx);
      await loadSubEntries(savedHeaderId);
    }
  }, [debugMode, loadSubEntries, headerEntryId]);

  const handleUnsplit = useCallback(async () => {
    if (!headerEntryId) return;
    if (!window.confirm('Unsplit this entry? The sub-entry fields will be merged back into the main entry.')) return;
    try {
      await api.unsplitExpenseEntry(headerEntryId);
      const updated = await api.getExpenseEntries();
      setEntries(updated);
      const editable = updated.filter(e => !e.ExpenseReportID && !e.HeaderEntryID);
      const headerIdx = editable.findIndex(e => e.ExpenseEntryID === headerEntryId);
      if (headerIdx >= 0) setCurrentIndex(headerIdx);
      setSubEntries([]);
      setEditingSubEntryId(null);
    } catch (e) {
      alert(e.message || 'Failed to unsplit entry');
    }
  }, [headerEntryId]);

  // Copy Split: fetch recent splits and show picker modal
  const handleCopySplitOpen = useCallback(async () => {
    if (!headerEntryId) return;
    setCopySplitLoading(true);
    try {
      const splits = await api.getRecentSplits();
      // Exclude the current split from the list
      const filtered = splits.filter(s => s.headerEntryId !== headerEntryId);
      if (filtered.length === 0) {
        alert('No other split expenses found in the past 90 days.');
        setCopySplitLoading(false);
        return;
      }
      setCopySplitOptions(filtered);
    } catch (e) {
      alert(e.message || 'Failed to load recent splits');
    }
    setCopySplitLoading(false);
  }, [headerEntryId]);

  // Apply a copied split: delete current subs, create new subs matching the template
  const handleApplyCopySplit = useCallback(async (split) => {
    if (!headerEntryId) return;
    setCopySplitOptions(null);
    try {
      // Delete all current sub-entries
      for (const sub of subEntries) {
        await api.deleteExpenseEntry(sub.ExpenseEntryID, debugMode);
      }

      // Filter template sub-entries: skip lines where job or task is closed
      const validLines = split.subEntries.filter(s => {
        if (s.jobId && !s.jobActive) return false;
        if (s.jobTaskId && !s.taskActive) return false;
        return true;
      });

      if (validLines.length === 0) {
        alert('All lines in the copied split have closed jobs/tasks. No lines were created.');
        await refreshEntries();
        await loadSubEntries(headerEntryId);
        return;
      }

      // Create sub-entries one at a time matching the template
      for (const line of validLines) {
        await api.addSubEntry(headerEntryId);
      }

      // Refresh to get the newly created sub-entry IDs
      await refreshEntries();
      const newSubs = await api.getSubEntries(headerEntryId);

      // Update each new sub-entry with the template's job/task/type
      for (let i = 0; i < Math.min(newSubs.length, validLines.length); i++) {
        const template = validLines[i];
        await api.updateExpenseEntry(newSubs[i].ExpenseEntryID, {
          entryDate: newSubs[i].EntryDate,
          jobId: template.jobId || null,
          jobTaskId: template.jobTaskId || null,
          rateSheetCode: template.rateSheetCode || null,
          rateCodeId: template.rateCodeId || null,
          nonBillable: template.nonBillable || false,
          nonReimbursable: newSubs[i].NonReimbursable || false,
          quantity: newSubs[i].Quantity,
          unitPrice: newSubs[i].UnitPrice,
          totalPrice: newSubs[i].TotalPrice,
          description: newSubs[i].Description,
        }, debugMode);
      }

      await refreshEntries();
      await loadSubEntries(headerEntryId);
    } catch (e) {
      alert(e.message || 'Failed to copy split');
      await refreshEntries();
      await loadSubEntries(headerEntryId);
    }
  }, [headerEntryId, subEntries, debugMode, refreshEntries, loadSubEntries]);

  // Navigate to a sub-entry's parent header in the entries list and edit the sub
  const handleEditSubEntry = useCallback((subEntryId) => {
    setEditingSubEntryId(subEntryId);
  }, []);

  // Navigate from Submit tab to Entry tab to edit a specific expense
  const handleEditFromSubmit = useCallback(async (entryId) => {
    await autoDeleteEmptyEntry(currentEntryRef.current);
    // editableEntries already excludes sub-entries, so find directly
    const idx = editableEntries.findIndex(e => e.ExpenseEntryID === entryId);
    if (idx >= 0) {
      setCurrentIndex(idx);
      setEditingSubEntryId(null);
      setActiveTab('entries');
    }
  }, [editableEntries, autoDeleteEmptyEntry]);

  // Navigate entries with auto-save then auto-delete of empty entries
  const handleNavEntry = useCallback(async (newIndex) => {
    // Auto-save any unsaved changes before navigating
    if (entryCardRef.current?.saveIfDirty) {
      await entryCardRef.current.saveIfDirty();
    }
    const prev = currentEntryRef.current;
    setEditingSubEntryId(null);
    setCurrentIndex(newIndex);

    // Auto-delete the entry we just navigated away from if it's empty
    if (prev && isEntryEmpty(prev)) {
      try {
        await api.deleteExpenseEntry(prev.ExpenseEntryID, debugMode);
        const updated = await api.getExpenseEntries();
        const updEditable = updated.filter(e => !e.ExpenseReportID && !e.HeaderEntryID);
        setEntries(updated);
        // Adjust index if needed
        setCurrentIndex(i => Math.min(i, Math.max(updEditable.length - 1, 0)));
      } catch (e) { /* ignore */ }
    }
  }, [entries]);

  // ============================================================
  // Receipts tab handlers
  // ============================================================
  const addDebugLog = useCallback((action, data) => {
    if (!debugMode) return;
    setReceiptDebugLogs(prev => [
      { timestamp: new Date().toISOString(), action, ...data },
      ...prev,
    ]);
  }, [debugMode]);

  const handleUploadReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await api.uploadReceipt(file, null, debugMode);
      if (debugMode && result?._debug) {
        console.log('[Receipt Upload Debug]', result._debug);
        addDebugLog('upload_success', { debug: result._debug });
      }
      await refreshReceipts();
    } catch (err) {
      if (debugMode) {
        addDebugLog('upload_error', { error: err.message, details: err._debug || null });
      }
      alert(err.message || 'Failed to upload receipt');
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleDropReceipts = async (files, entryId) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        await api.uploadReceipt(file, entryId || null, debugMode);
      }
      await refreshReceipts();
    } catch (err) {
      alert(err.message || 'Failed to upload receipt');
    }
    setUploading(false);
  };

  useEffect(() => {
    if (activeTab !== 'receipts') return;
    const handlePaste = async (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const blob = item.getAsFile();
          if (blob) {
            const ext = item.type.split('/')[1] || 'png';
            const file = new File([blob], `pasted-receipt-${Date.now()}.${ext}`, { type: item.type });
            await handleDropReceipts([file]);
          }
          return;
        }
      }
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [activeTab, debugMode]); // handleDropReceipts uses debugMode

  const handleAssignReceipt = async (receiptId, entryIds) => {
    try {
      await api.assignReceipt(receiptId, entryIds);
      await Promise.all([refreshReceipts(), refreshUnsubmitted(), refreshEntries()]);
      setAssignReceiptId(null);
    } catch (err) {
      alert(err.message || 'Failed to assign receipt');
    }
  };

  const handleDeleteReceipt = async (receiptId) => {
    if (!window.confirm('Delete this receipt? This will also remove it from SharePoint.')) return;
    try {
      const result = await api.deleteReceipt(receiptId, debugMode);
      if (debugMode && result?._debug) {
        console.log('[Receipt Delete Debug]', result._debug);
        addDebugLog('delete_success', { receiptId, debug: result._debug });
      }
      await refreshReceipts();
    } catch (err) {
      if (debugMode) {
        addDebugLog('delete_error', { receiptId, error: err.message });
      }
      alert(err.message || 'Failed to delete receipt');
    }
  };

  const handleAutoMatch = async () => {
    setAutoMatching(true);
    try {
      const result = await api.autoMatchReceipts();
      if (result.matches && result.matches.length > 0) {
        setAutoMatchResults(result);
        setAutoMatchSelected(new Set(result.matches.map((_, i) => i)));
      } else {
        alert(result.message || `No matches found. (${result.stats?.unmatchedReceipts || 0} unmatched receipts, ${result.stats?.unmatchedEntries || 0} unmatched entries)`);
      }
    } catch (err) {
      alert(err.message || 'Auto-match failed');
    }
    setAutoMatching(false);
  };

  const handleApplyAutoMatches = async () => {
    if (!autoMatchResults || autoMatchSelected.size === 0) return;
    setApplyingMatches(true);
    const selected = autoMatchResults.matches.filter((_, i) => autoMatchSelected.has(i));
    let successCount = 0;
    for (const match of selected) {
      try {
        await api.assignReceipt(match.receiptId, [match.entryId]);
        successCount++;
      } catch (err) {
        console.error(`Failed to assign receipt ${match.receiptId}:`, err);
      }
    }
    setAutoMatchResults(null);
    setAutoMatchSelected(new Set());
    setApplyingMatches(false);
    await Promise.all([refreshReceipts(), refreshEntries(), refreshUnsubmitted()]);
    alert(`${successCount} of ${selected.length} matches applied.`);
  };

  // ============================================================
  // Submission tab handlers
  // ============================================================

  // For split entries: a sub-entry is complete if it has job/task/type/amount
  const isSubEntryComplete = (e) => {
    if (!e.TotalPrice || parseFloat(e.TotalPrice) === 0) return false;
    if (!e.JobID || !e.JobTaskID) return false;
    if (!e.RateCodeID) return false;
    return true;
  };

  // Check if a split group is complete: all sub-entries complete + amounts balanced
  const isSplitGroupComplete = (header, subs) => {
    if (!header.EntryDate) return false;
    if (!header.Description) return false;
    if (!header.TotalPrice || parseFloat(header.TotalPrice) === 0) return false;
    if (subs.length === 0) return false;
    const headerTotal = parseFloat(header.TotalPrice) || 0;
    const subTotal = subs.reduce((sum, s) => sum + (parseFloat(s.TotalPrice) || 0), 0);
    if (Math.abs(headerTotal - subTotal) >= 0.005) return false;
    return subs.every(s => isSubEntryComplete(s));
  };

  const isEntryComplete = (e) => {
    // For header entries of a split group, check the group
    const subs = unsubmittedEntries.filter(s => s.HeaderEntryID === e.ExpenseEntryID);
    if (subs.length > 0) return isSplitGroupComplete(e, subs);
    // Sub-entries are not independently completable - they're part of a group
    if (e.HeaderEntryID) return false;
    // Regular entry
    if (!e.EntryDate) return false;
    if (!e.TotalPrice || parseFloat(e.TotalPrice) === 0) return false;
    if (!e.Description) return false;
    if (!e.JobID || !e.JobTaskID) return false;
    if (!e.RateCodeID) return false;
    return true;
  };

  const getMissingFields = (e) => {
    const subs = unsubmittedEntries.filter(s => s.HeaderEntryID === e.ExpenseEntryID);
    if (subs.length > 0) {
      // Split group header
      const missing = [];
      if (!e.EntryDate) missing.push('[No Date]');
      if (!e.Description) missing.push('[No Description]');
      if (!e.TotalPrice || parseFloat(e.TotalPrice) === 0) missing.push('[Amount = $0]');
      const headerTotal = parseFloat(e.TotalPrice) || 0;
      const subTotal = subs.reduce((sum, s) => sum + (parseFloat(s.TotalPrice) || 0), 0);
      if (Math.abs(headerTotal - subTotal) >= 0.005) {
        missing.push(`[Unbalanced: ${formatCurrency(subTotal)} of ${formatCurrency(headerTotal)}]`);
      }
      const incompleteSubs = subs.filter(s => !isSubEntryComplete(s));
      if (incompleteSubs.length > 0) missing.push(`[${incompleteSubs.length} incomplete sub-entry(ies)]`);
      return missing;
    }
    const missing = [];
    if (!e.EntryDate) missing.push('[No Date]');
    if (!e.Description) missing.push('[No Description]');
    if (!e.JobID || !e.JobTaskID) missing.push('[No Job/Job Task]');
    if (!e.RateCodeID) missing.push('[No Type]');
    if (!e.TotalPrice || parseFloat(e.TotalPrice) === 0) missing.push('[Amount = $0]');
    return missing;
  };

  // For the Summary tab, only show header entries and standalone entries (not sub-entries)
  const summaryEntries = unsubmittedEntries.filter(e => !e.HeaderEntryID);

  const toggleSelect = (id) => {
    // For split group headers, selecting the header selects/deselects the group
    const subs = unsubmittedEntries.filter(s => s.HeaderEntryID === id);
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        subs.forEach(s => next.delete(s.ExpenseEntryID));
      } else {
        next.add(id);
        subs.forEach(s => next.add(s.ExpenseEntryID));
      }
      return next;
    });
  };

  // Multi-assign: load jobs for customer (dropdown mode)
  useEffect(() => {
    if (!multiAssignMode || maFreeform || !maCustomerId) { setMaJobs([]); return; }
    setMaLoadingJobs(true);
    api.getJobs(maCustomerId).then(setMaJobs).finally(() => setMaLoadingJobs(false));
  }, [multiAssignMode, maFreeform, maCustomerId]);

  // Multi-assign: load tasks for selected job
  useEffect(() => {
    if (!multiAssignMode || !maJobId) { setMaTasks([]); setMaJobTaskId(''); return; }
    setMaLoadingTasks(true);
    api.getJobTasks(maJobId, { taskCode: 'Expense' }).then((t) => {
      setMaTasks(t);
      if (t.length === 1) setMaJobTaskId(t[0].JobTaskID);
    }).finally(() => setMaLoadingTasks(false));
  }, [multiAssignMode, maJobId]);

  // Multi-assign: load all jobs (freeform mode)
  useEffect(() => {
    if (!multiAssignMode || !maFreeform || maAllJobs.length > 0) return;
    setMaLoadingAllJobs(true);
    api.getJobs().then(setMaAllJobs).finally(() => setMaLoadingAllJobs(false));
  }, [multiAssignMode, maFreeform, maAllJobs.length]);

  // Multi-assign: freeform job selected → load tasks
  useEffect(() => {
    if (!multiAssignMode || !maFreeform || !maSelectedFreeformJob) return;
    setMaJobId(maSelectedFreeformJob.JobID);
    setMaJobTaskId('');
    setMaLoadingTasks(true);
    api.getJobTasks(maSelectedFreeformJob.JobID, { taskCode: 'Expense' }).then((t) => {
      setMaTasks(t);
      if (t.length === 1) setMaJobTaskId(t[0].JobTaskID);
    }).finally(() => setMaLoadingTasks(false));
  }, [multiAssignMode, maFreeform, maSelectedFreeformJob]);

  const maFilteredJobs = useMemo(() => {
    if (!maFreeform) return [];
    const q = maSearchQuery.toLowerCase().trim();
    if (!q) return recentJobs.slice(0, 30);
    return maAllJobs.filter((j) => {
      const custName = customers.find(c => c.CustomerID === j.CustomerID)?.CustomerName || '';
      return `${j.JobID} ${j.JobName} ${custName}`.toLowerCase().includes(q);
    }).slice(0, 30);
  }, [maFreeform, maAllJobs, maSearchQuery, customers, recentJobs]);

  // Multi-assign: derive ratesheet from selected job for expense type filtering
  const maRateSheetCode = useMemo(() => {
    if (!maJobId) return '';
    const jobList = maFreeform ? maAllJobs : maJobs;
    const job = jobList.find(j => j.JobID === maJobId) || recentJobs.find(j => j.JobID === maJobId);
    return job?.RateSheetCode || '';
  }, [maJobId, maFreeform, maAllJobs, maJobs, recentJobs]);

  const maFilteredRateCodes = useMemo(() => {
    if (!maRateSheetCode) return rateCodes.filter(rc => rc.RateType === 'Expense');
    return rateCodes.filter(rc => rc.RateSheetCode === maRateSheetCode);
  }, [rateCodes, maRateSheetCode]);

  const maTopRateCodes = useMemo(() => {
    if (!topExpenseTypes || topExpenseTypes.length === 0 || maFilteredRateCodes.length === 0) return [];
    const rankMap = new Map();
    topExpenseTypes.forEach((t, i) => rankMap.set(`${t.RateSheetCode}|${t.RateCodeID}`, i));
    return maFilteredRateCodes
      .filter(rc => rankMap.has(`${rc.RateSheetCode}|${rc.RateCodeID}`))
      .sort((a, b) => {
        return rankMap.get(`${a.RateSheetCode}|${a.RateCodeID}`) - rankMap.get(`${b.RateSheetCode}|${b.RateCodeID}`);
      });
  }, [maFilteredRateCodes, topExpenseTypes]);

  const maToggleSelect = (id) => {
    const subs = unsubmittedEntries.filter(s => s.HeaderEntryID === id);
    setMaSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        subs.forEach(s => next.delete(s.ExpenseEntryID));
      } else {
        next.add(id);
        subs.forEach(s => next.add(s.ExpenseEntryID));
      }
      return next;
    });
  };

  const maToggleSelectAll = () => {
    const allIds = new Set();
    summaryEntries.forEach(e => {
      allIds.add(e.ExpenseEntryID);
      unsubmittedEntries.filter(s => s.HeaderEntryID === e.ExpenseEntryID)
        .forEach(s => allIds.add(s.ExpenseEntryID));
    });
    setMaSelectedIds(prev => prev.size === allIds.size ? new Set() : allIds);
  };

  const handleMultiAssign = async () => {
    if (maSelectedIds.size === 0) return;
    const rsParts = maRateCodeValue.split('|');
    const hasJob = maJobId && maJobTaskId;
    const hasType = rsParts[0] && rsParts[1];
    if (!hasJob && !hasType) { alert('Select a job/task and/or expense type to assign.'); return; }
    setMaApplying(true);
    try {
      await api.bulkAssignExpenseEntries({
        entryIds: [...maSelectedIds],
        ...(hasJob ? { jobId: maJobId, jobTaskId: maJobTaskId } : {}),
        ...(hasType ? { rateSheetCode: rsParts[0], rateCodeId: rsParts[1] } : {}),
      });
      setMaSelectedIds(new Set());
      await refreshUnsubmitted();
      await refreshEntries();
    } catch (err) {
      alert(err.message || 'Failed to assign');
    }
    setMaApplying(false);
  };

  const toggleSelectAll = () => {
    const completable = summaryEntries.filter(e => isEntryComplete(e));
    const allIds = new Set();
    completable.forEach(e => {
      allIds.add(e.ExpenseEntryID);
      unsubmittedEntries.filter(s => s.HeaderEntryID === e.ExpenseEntryID)
        .forEach(s => allIds.add(s.ExpenseEntryID));
    });
    if (selectedIds.size === allIds.size) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(allIds);
    }
  };

  // Submit debug logs state
  const [submitDebugLogs, setSubmitDebugLogs] = useState(null);

  // Reopen (unsubmit) debug logs state
  const [reopenDebugLogs, setReopenDebugLogs] = useState(null);
  // NAV debug info for report receipt attach/remove operations
  const [receiptNavDebug, setReceiptNavDebug] = useState(null);

  const handleSubmitExpenses = async () => {
    if (selectedIds.size === 0) return;
    // Count only header/standalone entries (not sub-entries) for the submission count
    const headerCount = [...selectedIds].filter(id => !unsubmittedEntries.find(e => e.ExpenseEntryID === id)?.HeaderEntryID).length;
    if (headerCount === 0) {
      alert('No expense entries selected for submission.');
      return;
    }
    setSubmitting(true);
    setSubmitDebugLogs(null);
    try {
      // For submission, include all sub-entries but exclude header entries (per requirement #12)
      const idsToSubmit = [...selectedIds];
      const result = await api.submitExpenseReport(idsToSubmit, debugMode);
      if (debugMode && result?.apiResults) {
        console.log('[Expense Submit Debug]', result.apiResults);
        setSubmitDebugLogs(result.apiResults);
      }
      setSelectedIds(new Set());
      await refreshUnsubmitted();
      await refreshEntries();
      handleTabSwitch('reports');
    } catch (err) {
      if (debugMode && err.responseBody?.apiResults) {
        console.log('[Expense Submit Debug - Error]', err.responseBody.apiResults);
        setSubmitDebugLogs(err.responseBody.apiResults);
      }
      alert(err.message || 'Failed to submit expenses');
    }
    setSubmitting(false);
  };

  const sortedUnsubmitted = [...summaryEntries].sort((a, b) => {
    if (sortBy === 'date') {
      return (a.EntryDate || '').localeCompare(b.EntryDate || '');
    }
    const aKey = `${a.JobID || ''}|${a.JobTaskID || ''}`;
    const bKey = `${b.JobID || ''}|${b.JobTaskID || ''}`;
    return aKey.localeCompare(bKey);
  });

  // ============================================================
  // Reports tab handlers
  // ============================================================
  const handleViewReport = async (reportId) => {
    setLoadingReportDetail(true);
    setAddReceiptEntryId(null);
    try {
      const data = await api.getExpenseReport(reportId);
      setSelectedReport(data);
      // Load receipts so the "unused receipts" picker is populated
      refreshReceipts();
    } catch (e) {
      alert(e.message || 'Failed to load report');
    }
    setLoadingReportDetail(false);
  };

  // Reload the currently-open report detail without toggling the full-page spinner
  const reloadSelectedReport = useCallback(async (reportId) => {
    try {
      const data = await api.getExpenseReport(reportId);
      setSelectedReport(data);
    } catch (e) { console.error(e); }
  }, []);

  // Build a preview URL that busts the browser cache after an image edit
  const receiptPreviewSrc = useCallback(
    (receiptId) => `/api/expense-receipts/${receiptId}/preview?v=${imageVersion}`,
    [imageVersion]
  );

  const openReceiptEditor = useCallback((receiptId) => {
    setEditorReceiptId(receiptId);
  }, []);

  // Save the edited (rotated/cropped) image; overwrites the receipt in SharePoint
  const handleSaveEditedImage = useCallback(async (blob) => {
    if (!editorReceiptId) return;
    await api.editReceiptImage(editorReceiptId, blob, debugMode);
    // Bump the version so all previews re-fetch the new image, then refresh data
    setImageVersion((v) => v + 1);
    setEditorReceiptId(null);
    await refreshReceipts();
    if (selectedReport?.ExpenseReportID) {
      await reloadSelectedReport(selectedReport.ExpenseReportID);
    }
  }, [editorReceiptId, debugMode, refreshReceipts, selectedReport, reloadSelectedReport]);

  // Remove a receipt from a report expense item; NAV link is removed and the
  // receipt returns to the Receipts tab as unattached
  const handleRemoveReceiptFromReport = async (reportId, receiptId) => {
    // In debug mode, skip the confirm so the debug panel isn't blocked
    if (!debugMode && !window.confirm('Remove this receipt from the expense item? It will be moved back to the Receipts tab as unattached.')) return;
    // Optimistically show "Removing Receipt..." in place of this receipt
    setRemovingReceiptIds((prev) => new Set(prev).add(receiptId));
    setReceiptNavDebug(null);
    try {
      const result = await api.removeReceiptFromReport(reportId, receiptId, debugMode);
      if (debugMode && result?.apiResults) {
        setReceiptNavDebug({ op: 'RemoveEELReceipt', results: result.apiResults });
      }
      // On success the receipt is gone from the report after reload
      await Promise.all([reloadSelectedReport(reportId), refreshReceipts()]);
    } catch (e) {
      // On failure, roll back: the receipt info reappears once the flag clears
      if (debugMode) {
        setReceiptNavDebug({ op: 'RemoveEELReceipt', error: e.message, results: e.responseBody?.apiResults || [] });
      } else {
        alert(e.message || 'Failed to remove receipt');
      }
    } finally {
      setRemovingReceiptIds((prev) => {
        const next = new Set(prev);
        next.delete(receiptId);
        return next;
      });
    }
  };

  // Unused receipts available to attach (not linked to any entry)
  const unusedReceipts = receipts.filter(r => !r.ExpenseEntryID);

  // Attach an already-existing (unused) receipt to a report line + push to NAV
  const handleAttachExistingReceipt = async (reportId, entryId, receiptId) => {
    setAttachingReceipt(true);
    setReceiptNavDebug(null);
    try {
      const result = await api.attachReceiptToReport(reportId, { receiptId, expenseEntryId: entryId }, debugMode);
      if (debugMode && result?.apiResults) {
        setReceiptNavDebug({ op: 'AddEELReceipt', results: result.apiResults });
      }
      await Promise.all([reloadSelectedReport(reportId), refreshReceipts()]);
      setAddReceiptEntryId(null);
    } catch (e) {
      if (debugMode) {
        setReceiptNavDebug({ op: 'AddEELReceipt', error: e.message, results: e.responseBody?.apiResults || [] });
      } else {
        alert(e.message || 'Failed to attach receipt');
      }
    }
    setAttachingReceipt(false);
  };

  // Upload a new receipt file, then attach it to a report line + push to NAV
  const handleUploadReceiptToReport = async (reportId, entryId, file) => {
    setAttachingReceipt(true);
    setReceiptNavDebug(null);
    try {
      const uploaded = await api.uploadReceipt(file, null, debugMode);
      const result = await api.attachReceiptToReport(reportId, { receiptId: uploaded.ExpenseReceiptID, expenseEntryId: entryId }, debugMode);
      if (debugMode && result?.apiResults) {
        setReceiptNavDebug({ op: 'AddEELReceipt', results: result.apiResults });
      }
      await Promise.all([reloadSelectedReport(reportId), refreshReceipts()]);
      setAddReceiptEntryId(null);
    } catch (e) {
      if (debugMode) {
        setReceiptNavDebug({ op: 'AddEELReceipt', error: e.message, results: e.responseBody?.apiResults || [] });
      } else {
        alert(e.message || 'Failed to upload receipt');
      }
    }
    setAttachingReceipt(false);
  };

  // Per-line "Add Receipt" control shown on each expense item in a report detail
  // Clickable thumbnails of receipts already attached to a report expense item
  const renderAttachedReceipts = (entry) => {
    const list = (selectedReport?.receipts || []).filter(r => r.ExpenseEntryID === entry.ExpenseEntryID);
    if (list.length === 0) return null;
    return (
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
        {list.map((r) => {
          const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(r.ReceiptPath);
          const previewUrl = receiptPreviewSrc(r.ExpenseReceiptID);
          if (removingReceiptIds.has(r.ExpenseReceiptID)) {
            return (
              <div key={r.ExpenseReceiptID} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '44px', height: '44px', padding: '0 8px' }}>
                <span style={{ color: '#dc2626', fontSize: '12px', fontWeight: 600 }}>Removing Receipt…</span>
              </div>
            );
          }
          return (
            <div key={r.ExpenseReceiptID} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
              <div
                onClick={() => { setReceiptPreviewIsPdf(!isImage); setReceiptPreviewUrl(previewUrl); setReceiptPreviewId(r.ExpenseReceiptID); }}
                title={`Preview ${r.ReceiptPath.split('/').pop()}`}
                style={{ width: '44px', height: '44px', cursor: 'pointer', borderRadius: '4px', overflow: 'hidden', border: '1px solid var(--gray-200)', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                {isImage ? (
                  <img src={previewUrl} alt="Receipt" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
                ) : (
                  <span style={{ fontSize: '10px', color: 'var(--gray-500)' }}>PDF</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                {isImage && (
                  <button
                    onClick={() => openReceiptEditor(r.ExpenseReceiptID)}
                    title="Rotate or crop image"
                    style={{ background: 'none', border: 'none', color: 'var(--primary, #2563eb)', cursor: 'pointer', fontSize: '10px', padding: 0 }}
                  >&#9998; Edit</button>
                )}
                <button
                  onClick={() => handleRemoveReceiptFromReport(selectedReport?.ExpenseReportID, r.ExpenseReceiptID)}
                  disabled={attachingReceipt}
                  title="Remove receipt from this expense item"
                  style={{ background: 'none', border: 'none', color: 'var(--danger, #dc2626)', cursor: 'pointer', fontSize: '10px', padding: 0 }}
                >&times; Remove</button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderAddReceiptControl = (entry) => {
    const reportId = selectedReport?.ExpenseReportID;
    const isOpen = addReceiptEntryId === entry.ExpenseEntryID;
    const hasReceipt = entry.ReceiptCount > 0;
    if (!isOpen) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
          {hasReceipt && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '12px', fontWeight: 600, color: '#16a34a' }}>
              <span style={{ fontSize: '14px' }}>&#x2713;</span>
              Receipt Attached
            </span>
          )}
          <button
            className="btn btn-sm btn-outline"
            onClick={() => setAddReceiptEntryId(entry.ExpenseEntryID)}
            style={{ fontSize: '11px', padding: '2px 8px' }}
          >+ Add Receipt</button>
        </div>
      );
    }
    return (
      <div style={{ marginTop: '6px', background: 'white', padding: '8px', borderRadius: '6px', border: '1px solid var(--gray-200)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600 }}>
            Add receipt to this item
            {hasReceipt && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', color: '#16a34a' }}>
                <span style={{ fontSize: '14px' }}>&#x2713;</span>
                Receipt Attached
              </span>
            )}
          </span>
          <button
            className="btn btn-sm btn-outline"
            disabled={attachingReceipt}
            onClick={() => setAddReceiptEntryId(null)}
            style={{ fontSize: '10px', padding: '1px 6px' }}
          >Cancel</button>
        </div>

        <label
          className="btn btn-sm btn-primary"
          style={{ fontSize: '11px', padding: '3px 10px', display: 'inline-block', cursor: attachingReceipt ? 'default' : 'pointer', opacity: attachingReceipt ? 0.6 : 1 }}
        >
          {attachingReceipt ? 'Working…' : 'Upload New Receipt'}
          <input
            type="file"
            accept="image/*,.pdf,.eml,.msg"
            disabled={attachingReceipt}
            style={{ display: 'none' }}
            onChange={(ev) => { const f = ev.target.files?.[0]; ev.target.value = ''; if (f) handleUploadReceiptToReport(reportId, entry.ExpenseEntryID, f); }}
          />
        </label>

        <div style={{ fontSize: '11px', color: 'var(--gray-500)', margin: '8px 0 4px' }}>
          {unusedReceipts.length > 0 ? 'or select an unused receipt:' : 'or select an unused receipt (none available)'}
        </div>
        {unusedReceipts.length > 0 && (
          <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: '4px' }}>
            {unusedReceipts.map((r) => {
              const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(r.ReceiptPath);
              const previewUrl = receiptPreviewSrc(r.ExpenseReceiptID);
              return (
                <div key={r.ExpenseReceiptID} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '4px 6px', borderBottom: '1px solid var(--gray-100)' }}>
                  <div
                    onClick={() => { setReceiptPreviewIsPdf(!isImage); setReceiptPreviewUrl(previewUrl); setReceiptPreviewId(r.ExpenseReceiptID); }}
                    style={{ flexShrink: 0, width: '36px', height: '36px', cursor: 'pointer', borderRadius: '4px', overflow: 'hidden', border: '1px solid var(--gray-200)', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    {isImage ? (
                      <img src={previewUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <span style={{ fontSize: '9px', color: 'var(--gray-500)' }}>PDF</span>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, fontSize: '11px', color: 'var(--gray-600)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.ReceiptPath.split('/').pop()}
                    {r.ExtractedAmount != null && <span style={{ color: 'var(--gray-400)' }}> — {formatCurrency(r.ExtractedAmount)}</span>}
                  </div>
                  {isImage && (
                    <button
                      className="btn btn-sm btn-outline"
                      disabled={attachingReceipt}
                      onClick={() => openReceiptEditor(r.ExpenseReceiptID)}
                      title="Rotate or crop image"
                      style={{ fontSize: '10px', padding: '2px 6px' }}
                    >&#9998;</button>
                  )}
                  <button
                    className="btn btn-sm btn-primary"
                    disabled={attachingReceipt}
                    onClick={() => handleAttachExistingReceipt(reportId, entry.ExpenseEntryID, r.ExpenseReceiptID)}
                    style={{ fontSize: '10px', padding: '2px 8px' }}
                  >Attach</button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const handleUnsubmit = async (reportId) => {
    if (!window.confirm('Reopen this expense report? All entries will be returned to draft.')) return;
    setReopenDebugLogs(null);
    try {
      const result = await api.unsubmitExpenseReport(reportId, debugMode);
      if (debugMode && result?._debug) {
        console.log('[Expense Reopen Debug]', result._debug);
        setReopenDebugLogs(result._debug);
      }
      setSelectedReport(null);
      await refreshReports(reportsPage);
      await refreshEntries();
      handleTabSwitch('submission');
    } catch (e) {
      if (debugMode && e.responseBody?._debug) {
        console.log('[Expense Reopen Debug - Error]', e.responseBody._debug);
        setReopenDebugLogs(e.responseBody._debug);
      }
      alert(e.message || 'Failed to reopen report');
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner" />Loading expenses...</div>;
  }

  return (
    <>
      <div className="tab-bar">
        <button className={activeTab === 'submission' ? 'active' : ''} onClick={() => handleTabSwitch('submission')}>Summary</button>
        <button className={activeTab === 'entries' ? 'active' : ''} onClick={() => handleTabSwitch('entries')}>Entry</button>
        <button className={activeTab === 'receipts' ? 'active' : ''} onClick={() => handleTabSwitch('receipts')}>Receipts</button>
        <button className={activeTab === 'reports' ? 'active' : ''} onClick={() => handleTabSwitch('reports')}>Reports</button>
      </div>

      {/* ============================================================
          ENTRIES TAB
          ============================================================ */}
      {activeTab === 'entries' && (
        <>
          {/* Hide "+ New Expense" when split callout is shown (Fix 5) */}
          {!isSplitContext && (
            <div style={{ padding: '12px 16px', display: 'flex', gap: '8px' }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleNewExpense}>
                + New Expense
              </button>
            </div>
          )}

          {editableEntries.length === 0 ? (
            <div className="empty-state">
              <p>No expense entries yet. Create one to get started.</p>
            </div>
          ) : (
            <>
              {/* Split entry callout and sub-entries table */}
              {isSplitContext && headerEntry && (
                <>
                  <SplitEntryPanel
                    headerEntry={headerEntry}
                    subEntries={subEntries}
                    loadingSubEntries={loadingSubEntries}
                    editingSubEntryId={editingSubEntryId}
                    onEditSubEntry={handleEditSubEntry}
                    onAddSubEntry={handleAddSubEntry}
                    onSaveSubEntry={handleSaveSubEntry}
                    onDeleteSubEntry={handleDeleteSubEntry}
                    onUnsplit={handleUnsplit}
                    onCopySplit={handleCopySplitOpen}
                    copySplitLoading={copySplitLoading}
                    rateCodes={rateCodes}
                    customers={customers}
                    isHeaderView={isHeaderEntry}
                    topExpenseTypes={topExpenseTypes}
                  />

                  {/* Next/Previous for a split expense: navigate between the
                      adjacent header/standalone expenses (not the sub-entries). */}
                  {user?.showExpenseNav && navSequence.length > 1 && (
                    <div className="entry-nav-row" style={{ margin: '10px 16px 0' }}>
                      <button
                        className="entry-nav-btn"
                        type="button"
                        disabled={navPos <= 0}
                        onClick={() => goNavEntry(-1)}
                      >&#9650; {navSequence[navPos - 1] ? entryNavLabel(navSequence[navPos - 1]) : 'Up'}</button>
                      <button
                        className="entry-nav-btn"
                        type="button"
                        disabled={navPos < 0 || navPos >= navSequence.length - 1}
                        onClick={() => goNavEntry(1)}
                      >&#9660; {navSequence[navPos + 1] ? entryNavLabel(navSequence[navPos + 1]) : 'Down'}</button>
                    </div>
                  )}

                </>
              )}

              {/* Regular entry card shown when NOT in split context */}
              {!isSplitContext && (
                <ExpenseEntryCard
                  ref={entryCardRef}
                  key={currentEntry?.ExpenseEntryID || 'none'}
                  entry={currentEntry}
                  rateCodes={rateCodes}
                  topExpenseTypes={topExpenseTypes}
                  recentJobs={recentJobs}
                  customers={customers}
                  isEditable={!!currentEntry && !currentEntry.ExpenseReportID}
                  onSave={handleSaveEntry}
                  onDelete={handleDeleteEntry}
                  onPrev={() => goNavEntry(-1)}
                  onNext={() => goNavEntry(1)}
                  showNav={!!user?.showExpenseNav}
                  entryIndex={navPos}
                  entryCount={navSequence.length}
                  entries={navSequence}
                  onSplit={currentEntry && !currentEntry.HeaderEntryID && currentEntry.TotalPrice && parseFloat(currentEntry.TotalPrice) > 0 ? handleSplitEntry : null}
                />
              )}

              {/* Receipt section for current entry */}
              {currentEntry?.ExpenseEntryID && (
                <EntryReceiptSection
                  entryId={currentEntry.ExpenseEntryID}
                  receipts={receipts}
                  onUpload={async (file) => {
                    // Auto-save any unsaved changes before uploading receipt
                    if (entryCardRef.current?.saveIfDirty) {
                      await entryCardRef.current.saveIfDirty();
                    }
                    setUploading(true);
                    try {
                      await api.uploadReceipt(file, currentEntry.ExpenseEntryID, debugMode);
                      await refreshReceipts();
                    } catch (err) {
                      alert(err.message || 'Failed to upload receipt');
                    }
                    setUploading(false);
                  }}
                  onDelete={async (receiptId) => {
                    if (!window.confirm('Delete this receipt?')) return;
                    try {
                      await api.deleteReceipt(receiptId, debugMode);
                      await refreshReceipts();
                    } catch (err) {
                      alert(err.message || 'Failed to delete receipt');
                    }
                  }}
                  uploading={uploading}
                  imageVersion={imageVersion}
                  onPreview={(url, isPdf, receiptId) => { setReceiptPreviewUrl(url); setReceiptPreviewIsPdf(isPdf); setReceiptPreviewId(receiptId || null); }}
                  onEditImage={openReceiptEditor}
                />
              )}
            </>
          )}

          {/* Debug panel for entry operations */}
          {debugMode && entryDebugLogs.length > 0 && (
            <div style={{ margin: '8px 16px', border: '1px solid var(--warning, #f59e0b)', borderRadius: '6px', background: '#fffbeb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #fde68a' }}>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#92400e' }}>Debug Log (Entry Operations)</span>
                <button
                  className="btn btn-sm btn-outline"
                  onClick={() => setEntryDebugLogs([])}
                  style={{ fontSize: '11px', padding: '2px 6px' }}
                >Clear</button>
              </div>
              <div style={{ padding: '8px 12px', maxHeight: '300px', overflowY: 'auto' }}>
                {entryDebugLogs.map((log, i) => (
                  <div key={i} style={{ marginBottom: '4px', fontSize: '12px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    <span style={{ color: '#92400e', fontWeight: 600 }}>[{log.step}]</span>{' '}
                    {log.error && <span style={{ color: '#dc2626' }}>Error: {log.error}</span>}
                    {log.entryId != null && <span>EntryID: {log.entryId} </span>}
                    {log.body && (
                      <div style={{ color: '#374151', marginLeft: '8px', marginTop: '2px' }}>
                        Body: {JSON.stringify(log.body, null, 2)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ============================================================
          RECEIPTS TAB
          ============================================================ */}
      {activeTab === 'receipts' && (
        <div style={{ padding: '16px' }}>
          <div
            style={{
              marginBottom: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center',
              ...(!isMobile ? (dragOver
                ? { padding: '12px', border: '2px dashed var(--primary)', borderRadius: '8px', background: 'var(--primary-light)' }
                : { padding: '12px', border: '2px dashed #93c5fd', borderRadius: '8px' }
              ) : {}),
            }}
            {...(!isMobile ? {
              onDragOver: (e) => { e.preventDefault(); setDragOver(true); },
              onDragLeave: () => setDragOver(false),
              onDrop: (e) => { e.preventDefault(); setDragOver(false); handleDropReceipts(Array.from(e.dataTransfer.files)); },
            } : {})}
          >
            <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
              {uploading ? 'Uploading...' : 'Upload / Take Photo'}
              <input
                type="file"
                accept="image/*,application/pdf,.eml,.msg,message/rfc822,application/vnd.ms-outlook"
                capture="environment"
                onChange={handleUploadReceipt}
                style={{ display: 'none' }}
                disabled={uploading}
              />
            </label>
            <button
              className="btn btn-outline"
              onClick={handleAutoMatch}
              disabled={autoMatching || receipts.length === 0}
            >
              {autoMatching ? 'Scanning...' : 'Auto-Match'}
            </button>
            {!isMobile && !dragOver && (
              <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>or drag files here</span>
            )}
            {!isMobile && dragOver && (
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--primary)' }}>Drop files to upload</span>
            )}
          </div>

          {loadingReceipts ? (
            <div className="loading"><div className="spinner" />Loading receipts...</div>
          ) : receipts.length === 0 ? (
            <div className="empty-state"><p>No receipts uploaded yet.</p></div>
          ) : (
            <div className="card">
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="card-title">Receipts</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer', color: 'var(--gray-600)', margin: 0 }}>
                  <span>Hide expenses with receipts</span>
                  <div
                    onClick={() => setHideMatchedReceipts(prev => !prev)}
                    style={{
                      width: '36px', height: '20px', borderRadius: '10px', position: 'relative', cursor: 'pointer',
                      background: hideMatchedReceipts ? 'var(--primary, #2563eb)' : 'var(--gray-300, #d1d5db)',
                      transition: 'background 0.2s',
                    }}
                  >
                    <div style={{
                      width: '16px', height: '16px', borderRadius: '50%', background: 'white', position: 'absolute',
                      top: '2px', left: hideMatchedReceipts ? '18px' : '2px', transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }} />
                  </div>
                </label>
              </div>
              {receipts.map((r) => {
                const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(r.ReceiptPath);
                const previewUrl = r.ReceiptPath.startsWith('/uploads/')
                  ? `${r.ReceiptPath}?v=${imageVersion}`
                  : receiptPreviewSrc(r.ExpenseReceiptID);

                return (
                  <div key={r.ExpenseReceiptID} style={{
                    padding: '10px 0', borderBottom: '1px solid var(--gray-100)',
                  }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      {/* Receipt preview thumbnail */}
                      {isImage && (
                        <div
                          style={{ flexShrink: 0, width: '60px', height: '60px', cursor: 'pointer', borderRadius: '4px', overflow: 'hidden', border: '1px solid var(--gray-200)' }}
                          onClick={() => { setReceiptPreviewIsPdf(false); setReceiptPreviewUrl(previewUrl); setReceiptPreviewId(r.ExpenseReceiptID); }}
                        >
                          <img
                            src={previewUrl}
                            alt="Receipt"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                      {!isImage && (
                        <div
                          style={{ flexShrink: 0, width: '60px', height: '60px', cursor: 'pointer', borderRadius: '4px', border: '1px solid var(--gray-200)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--gray-50)', fontSize: '11px', color: 'var(--gray-500)' }}
                          onClick={() => { setReceiptPreviewIsPdf(true); setReceiptPreviewUrl(previewUrl); setReceiptPreviewId(r.ExpenseReceiptID); }}
                        >
                          PDF
                        </div>
                      )}

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{ fontSize: '13px', fontWeight: 500, cursor: 'pointer', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '6px' }}
                          onClick={() => { setReceiptPreviewIsPdf(!isImage); setReceiptPreviewUrl(previewUrl); setReceiptPreviewId(r.ExpenseReceiptID); }}
                        >
                          {r.ExpenseEntryID && (
                            <span style={{ color: '#16a34a', fontSize: '15px', flexShrink: 0 }} title="Assigned to an expense entry">&#x2713;</span>
                          )}
                          <span>
                          {r.ReceiptPath.split('/').pop()}
                          {r.FileSize != null && (
                            <span style={{ fontWeight: 400, color: 'var(--gray-500)', marginLeft: '4px' }}>
                              ({r.FileSize < 1024 ? `${r.FileSize} B` : r.FileSize < 1048576 ? `${(r.FileSize / 1024).toFixed(1)} KB` : `${(r.FileSize / 1048576).toFixed(1)} MB`})
                            </span>
                          )}
                          </span>
                        </div>
                        {(r.ExtractedMerchant || r.ExtractedAmount != null) ? (
                          <div style={{ fontSize: '11px', color: 'var(--gray-500)', marginTop: '2px', fontStyle: 'italic' }}>
                            Auto-generated: {r.ExtractedMerchant || 'Unknown'} / {r.ExtractedAmount != null ? formatCurrency(r.ExtractedAmount) : 'Unknown'}
                          </div>
                        ) : (
                          <div style={{ fontSize: '11px', color: 'var(--gray-400)', marginTop: '2px', fontStyle: 'italic' }}>
                            Unable to auto-generate receipt information
                          </div>
                        )}
                        {/* Assignment info: date, job name, description, amount */}
                        {r.ExpenseEntryID ? (
                          <div style={{ fontSize: '12px', color: 'var(--gray-600)', marginTop: '2px' }}>
                            {r.LinkedEntries && r.LinkedEntries.length > 0 ? (
                              r.LinkedEntries.map((le, idx) => (
                                <div
                                  key={idx}
                                  style={{ marginTop: idx > 0 ? '2px' : 0, cursor: 'pointer', color: 'var(--primary)' }}
                                  onClick={() => handleEditFromSubmit(le.ExpenseEntryID)}
                                >
                                  {displayDate(le.EntryDate)}
                                  {le.JobName ? ` | ${le.JobName}` : ''}
                                  {le.Description ? ` | ${le.Description}` : ''}
                                  {` | ${formatCurrency(le.TotalPrice)}`}
                                </div>
                              ))
                            ) : (
                              <div
                                style={{ cursor: 'pointer', color: 'var(--primary)' }}
                                onClick={() => handleEditFromSubmit(r.ExpenseEntryID)}
                              >
                                {r.EntryDate ? displayDate(r.EntryDate) : ''}
                                {r.EntryJobName ? ` | ${r.EntryJobName}` : ''}
                                {r.EntryDescription ? ` | ${r.EntryDescription}` : ''}
                                {` | ${formatCurrency(r.EntryAmount)}`}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '2px' }}>
                            Unassigned
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                        {assignReceiptId === r.ExpenseReceiptID ? (
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => { setAssignReceiptId(null); setAssignSelectedEntries(new Set()); }}
                            style={{ fontSize: '11px', padding: '4px 8px' }}
                          >Cancel</button>
                        ) : (
                          <>
                            <button
                              className="btn btn-sm btn-outline"
                              onClick={() => {
                                setAssignReceiptId(r.ExpenseReceiptID);
                                // Pre-select currently assigned entry
                                setAssignSelectedEntries(r.ExpenseEntryID ? new Set([r.ExpenseEntryID]) : new Set());
                              }}
                              style={{ fontSize: '11px', padding: '4px 8px' }}
                            >{r.ExpenseEntryID ? 'Reassign' : 'Assign'}</button>
                            {isImage && (
                              <button
                                className="btn btn-sm btn-outline"
                                onClick={() => openReceiptEditor(r.ExpenseReceiptID)}
                                title="Rotate or crop image"
                                style={{ fontSize: '11px', padding: '4px 8px' }}
                              >&#9998; Edit</button>
                            )}
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDeleteReceipt(r.ExpenseReceiptID)}
                              style={{ fontSize: '11px', padding: '4px 8px' }}
                            >Delete</button>
                          </>
                        )}
                      </div>
                    </div>
                    {/* Assign panel – full width below the receipt row for small screen visibility */}
                    {assignReceiptId === r.ExpenseReceiptID && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', background: 'white', padding: '8px', marginTop: '6px', borderRadius: '6px', border: '1px solid var(--gray-200)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                        <div style={{ fontSize: '12px', fontWeight: 500, marginBottom: '2px' }}>Select expense(s):</div>
                        <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: '4px', background: 'white' }}>
                          {editableEntries
                            .filter(ee => {
                              if (!hideMatchedReceipts) return true;
                              if (assignSelectedEntries.has(ee.ExpenseEntryID)) return true;
                              const hasReceipt = receipts.some(rc => rc.ExpenseEntryID === ee.ExpenseEntryID);
                              return !hasReceipt;
                            })
                            .map(ee => (
                            <label key={ee.ExpenseEntryID} style={{
                              display: 'flex', alignItems: 'flex-start', gap: '6px', padding: '4px 8px',
                              borderBottom: '1px solid var(--gray-100)', cursor: 'pointer', fontSize: '12px',
                            }}>
                              <input
                                type="checkbox"
                                checked={assignSelectedEntries.has(ee.ExpenseEntryID)}
                                onChange={(e) => {
                                  setAssignSelectedEntries(prev => {
                                    const next = new Set(prev);
                                    if (e.target.checked) next.add(ee.ExpenseEntryID);
                                    else next.delete(ee.ExpenseEntryID);
                                    return next;
                                  });
                                }}
                                style={{ marginTop: '2px' }}
                              />
                              <span>{displayDate(ee.EntryDate)} - {(ee.Description || 'No desc').replace(/^Credit card charge by:\s*/i, '')} ({formatCurrency(ee.TotalPrice)})</span>
                            </label>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => {
                              const ids = [...assignSelectedEntries];
                              handleAssignReceipt(r.ExpenseReceiptID, ids.length > 0 ? ids : null);
                              setAssignSelectedEntries(new Set());
                            }}
                            style={{ fontSize: '11px', padding: '2px 6px', flex: 1 }}
                          >{assignSelectedEntries.size > 0 ? `Assign (${assignSelectedEntries.size})` : 'Unassign'}</button>
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={() => { setAssignReceiptId(null); setAssignSelectedEntries(new Set()); }}
                            style={{ fontSize: '11px', padding: '2px 6px', flex: 1 }}
                          >Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Debug panel for receipts */}
          {debugMode && (
            <div style={{ marginTop: '16px', border: '1px solid var(--warning, #f59e0b)', borderRadius: '6px', background: '#fffbeb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #fde68a' }}>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#92400e' }}>Debug Log (Receipts)</span>
                {receiptDebugLogs.length > 0 && (
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => setReceiptDebugLogs([])}
                    style={{ fontSize: '11px', padding: '2px 6px' }}
                  >Clear</button>
                )}
              </div>
              <div style={{ padding: '8px 12px', maxHeight: '300px', overflowY: 'auto' }}>
                {receiptDebugLogs.length === 0 ? (
                  <div style={{ fontSize: '12px', color: '#92400e' }}>
                    No debug events yet. Upload or delete a receipt to see debug output here.
                  </div>
                ) : (
                  receiptDebugLogs.map((log, i) => (
                    <div key={i} style={{ marginBottom: '8px', fontSize: '12px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      <div style={{ color: '#92400e', fontWeight: 600 }}>
                        [{log.timestamp}] {log.action}
                      </div>
                      {log.error && (
                        <div style={{ color: '#dc2626', marginLeft: '8px' }}>Error: {log.error}</div>
                      )}
                      {log.debug && (
                        <div style={{ color: '#374151', marginLeft: '8px' }}>
                          {log.debug.map((d, j) => (
                            <div key={j} style={{ marginBottom: '2px' }}>
                              [{d.step}] {d.error ? <span style={{ color: '#dc2626' }}>{d.error}</span> : d.success ? 'OK' : ''}{' '}
                              {d.status ? `(${d.status})` : ''}{' '}
                              {d.url ? d.url : ''}{' '}
                              {d.tokenLength ? `tokenLen=${d.tokenLength}` : ''}{' '}
                              {d.itemId ? `itemId=${d.itemId}` : ''}{' '}
                              {d.bufferSize ? `size=${d.bufferSize}` : ''}
                            </div>
                          ))}
                        </div>
                      )}
                      {log.details && (
                        <div style={{ color: '#374151', marginLeft: '8px' }}>
                          {JSON.stringify(log.details, null, 2)}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

        </div>
      )}

      {/* ============================================================
          SUBMISSION TAB
          ============================================================ */}
      {activeTab === 'submission' && (
        <div style={{ padding: '16px' }}>
          {loadingSubmission ? (
            <div className="loading"><div className="spinner" />Loading expenses...</div>
          ) : <>
          <div className="expense-summary-sticky">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', fontWeight: 600 }}>Unsubmitted Expenses</span>
              {!multiAssignMode && (
                <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>
                  ({[...selectedIds].filter(id => !unsubmittedEntries.find(e => e.ExpenseEntryID === id)?.HeaderEntryID).length} selected)
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                className={`btn btn-sm ${multiAssignMode ? 'btn-outline' : 'btn-outline'}`}
                style={{ fontSize: '11px', padding: '4px 10px', background: multiAssignMode ? 'var(--primary)' : undefined, color: multiAssignMode ? '#fff' : undefined }}
                onClick={() => {
                  setMultiAssignMode(!multiAssignMode);
                  setMaSelectedIds(new Set());
                  setMaJobId(''); setMaJobTaskId(''); setMaRateCodeValue('');
                  setMaCustomerId(''); setMaSearchQuery(''); setMaSelectedFreeformJob(null);
                  setMaFreeform(false);
                }}
              >
                {multiAssignMode ? 'Back to Submission Mode' : 'Multi-Assign Mode'}
              </button>
              {!multiAssignMode && (
                <label style={{ fontSize: '12px', display: 'flex', gap: '4px', alignItems: 'center' }}>
                  Sort:
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    style={{ fontSize: '12px', padding: '2px 4px' }}
                  >
                    <option value="date">Date</option>
                    <option value="job">Job/Task</option>
                  </select>
                </label>
              )}
            </div>
          </div>

          {/* Multi-Assign controls */}
          {multiAssignMode && summaryEntries.length > 0 && (
            <div style={{ marginBottom: '12px', padding: '12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#1d4ed8', marginBottom: '8px' }}>Bulk Assign Job/Task and/or Expense Type</div>

              {/* Job/Task selection */}
              <div style={{ marginBottom: '8px', padding: '8px', background: 'white', borderRadius: '4px', border: '1px solid var(--gray-200)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label style={{ fontWeight: 600, fontSize: '12px', margin: 0 }}>Job / Task</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', cursor: 'pointer', margin: 0 }}>
                    <input type="checkbox" checked={maFreeform} onChange={(e) => {
                      setMaFreeform(e.target.checked);
                      setMaSelectedFreeformJob(null); setMaSearchQuery('');
                      setMaJobId(''); setMaJobTaskId(''); setMaTasks([]); setMaRateCodeValue('');
                    }} />
                    Search
                  </label>
                </div>
                {!maFreeform ? (
                  <>
                    <select className="form-control" value={maCustomerId} onChange={(e) => { setMaCustomerId(e.target.value); setMaJobId(''); setMaJobTaskId(''); setMaRateCodeValue(''); }} style={{ fontSize: '12px', marginBottom: '4px' }}>
                      <option value="">Select customer...</option>
                      {customers.map(c => <option key={c.CustomerID} value={c.CustomerID}>{c.CustomerName}</option>)}
                    </select>
                    <select className="form-control" value={maJobId} onChange={(e) => {
                      const newJobId = e.target.value;
                      if (!maCustomerId && newJobId) {
                        const rj = recentJobs.find(j => j.JobID === newJobId);
                        if (rj) setMaCustomerId(rj.CustomerID);
                      }
                      setMaJobId(newJobId); setMaJobTaskId(''); setMaRateCodeValue('');
                    }} disabled={(!maCustomerId && recentJobs.length === 0) || maLoadingJobs} style={{ fontSize: '12px', marginBottom: '4px' }}>
                      <option value="">{maLoadingJobs ? 'Loading...' : 'Select job...'}</option>
                      {(() => {
                        if (!maCustomerId && recentJobs.length > 0) {
                          return (
                            <optgroup label="Recent Jobs">{recentJobs.map(j => <option key={`r-${j.JobID}`} value={j.JobID}>{j.JobID} - {j.JobName} ({j.CustomerName})</option>)}</optgroup>
                          );
                        }
                        const recentInCust = recentJobs.filter(rj => rj.CustomerID === maCustomerId && maJobs.some(j => j.JobID === rj.JobID));
                        if (recentInCust.length > 0) return (
                          <>
                            <optgroup label="Recent Jobs">{recentInCust.map(j => <option key={`r-${j.JobID}`} value={j.JobID}>{j.JobID} - {j.JobName}</option>)}</optgroup>
                            <optgroup label="All Jobs">{maJobs.map(j => <option key={j.JobID} value={j.JobID}>{j.JobID} - {j.JobName}</option>)}</optgroup>
                          </>
                        );
                        return maJobs.map(j => <option key={j.JobID} value={j.JobID}>{j.JobID} - {j.JobName}</option>);
                      })()}
                    </select>
                    {maTasks.length === 1 ? (
                      <div className="form-control" style={{ fontSize: '12px', background: 'white' }}>{maTasks[0].JobTaskID} – {maTasks[0].TaskName}</div>
                    ) : (
                      <select className="form-control" value={maJobTaskId} onChange={(e) => setMaJobTaskId(e.target.value)} disabled={!maJobId || maLoadingTasks} style={{ fontSize: '12px' }}>
                        <option value="">{maLoadingTasks ? 'Loading...' : 'Select task...'}</option>
                        {maTasks.map(t => <option key={t.JobTaskID} value={t.JobTaskID}>{t.JobTaskID} – {t.TaskName}</option>)}
                      </select>
                    )}
                  </>
                ) : (
                  <>
                    <input className="form-control" type="text" placeholder={maLoadingAllJobs ? 'Loading jobs...' : 'Type to search...'} value={maSearchQuery}
                      onChange={(e) => { setMaSearchQuery(e.target.value); setMaSelectedFreeformJob(null); setMaJobId(''); setMaJobTaskId(''); setMaRateCodeValue(''); }}
                      disabled={maLoadingAllJobs} style={{ fontSize: '12px', marginBottom: '4px' }} />
                    {maFilteredJobs.length > 0 && !maSelectedFreeformJob && (
                      <div style={{ maxHeight: '120px', overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: '4px', marginBottom: '4px', background: 'white' }}>
                        {!maSearchQuery.trim() && <div style={{ padding: '3px 8px', fontSize: '10px', fontWeight: 600, color: 'var(--gray-500)', background: 'var(--gray-50)', textTransform: 'uppercase', borderBottom: '1px solid var(--gray-200)' }}>Recent Jobs</div>}
                        {maFilteredJobs.map(j => {
                          const custName = customers.find(c => c.CustomerID === j.CustomerID)?.CustomerName || '';
                          return (
                            <div key={j.JobID} onClick={() => { setMaSelectedFreeformJob(j); setMaSearchQuery(`${j.JobID} - ${j.JobName}`); }}
                              style={{ padding: '4px 8px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid var(--gray-100)' }}
                              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--gray-50)'} onMouseLeave={(e) => e.currentTarget.style.background = 'white'}>
                              <div style={{ fontWeight: 500 }}>{j.JobID} - {j.JobName}</div>
                              {custName && <div style={{ fontSize: '10px', color: 'var(--gray-500)' }}>{custName}</div>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {maSelectedFreeformJob && (
                      <div style={{ fontSize: '11px', color: 'var(--gray-600)', marginBottom: '4px' }}>
                        Selected: {maSelectedFreeformJob.JobID} - {maSelectedFreeformJob.JobName}
                        <button onClick={() => { setMaSelectedFreeformJob(null); setMaSearchQuery(''); setMaJobId(''); setMaJobTaskId(''); setMaRateCodeValue(''); }}
                          style={{ marginLeft: '6px', background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '11px' }}>Clear</button>
                      </div>
                    )}
                    {maTasks.length === 1 ? (
                      <div className="form-control" style={{ fontSize: '12px', background: 'white' }}>{maTasks[0].JobTaskID} – {maTasks[0].TaskName}</div>
                    ) : maTasks.length > 1 ? (
                      <select className="form-control" value={maJobTaskId} onChange={(e) => setMaJobTaskId(e.target.value)} disabled={maLoadingTasks} style={{ fontSize: '12px' }}>
                        <option value="">{maLoadingTasks ? 'Loading...' : 'Select task...'}</option>
                        {maTasks.map(t => <option key={t.JobTaskID} value={t.JobTaskID}>{t.JobTaskID} – {t.TaskName}</option>)}
                      </select>
                    ) : null}
                  </>
                )}
                {maJobId && maJobTaskId && (
                  <div style={{ fontSize: '11px', color: '#16a34a', marginTop: '4px' }}>&#x2713; Job/Task selected</div>
                )}
              </div>

              {/* Expense type selection */}
              <div style={{ marginBottom: '8px', padding: '8px', background: 'white', borderRadius: '4px', border: '1px solid var(--gray-200)' }}>
                <label style={{ fontWeight: 600, fontSize: '12px', display: 'block', marginBottom: '4px' }}>Expense Type</label>
                <select className="form-control" value={maRateCodeValue} onChange={(e) => setMaRateCodeValue(e.target.value)} disabled={!maJobId || !maJobTaskId} style={{ fontSize: '12px' }}>
                  <option value="">{!maJobId || !maJobTaskId ? 'Select a job/task first...' : 'Select expense type...'}</option>
                  {maTopRateCodes.length > 0 && (
                    <optgroup label="Most Used">
                      {maTopRateCodes.map(rc => (
                        <option key={`top-${rc.RateSheetCode}|${rc.RateCodeID}`} value={`${rc.RateSheetCode}|${rc.RateCodeID}`}>
                          {rc.RateCodeName}{rc.Rate > 0 ? ` ($${parseFloat(rc.Rate).toFixed(2)}/unit)` : ''}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  <optgroup label={maTopRateCodes.length > 0 ? 'All Expense Types' : undefined}>
                    {maFilteredRateCodes.map(rc => (
                      <option key={`all-${rc.RateSheetCode}|${rc.RateCodeID}`} value={`${rc.RateSheetCode}|${rc.RateCodeID}`}>
                        {rc.RateCodeName}{rc.Rate > 0 ? ` ($${parseFloat(rc.Rate).toFixed(2)}/unit)` : ''}
                      </option>
                    ))}
                  </optgroup>
                </select>
                {maRateCodeValue && (
                  <div style={{ fontSize: '11px', color: '#16a34a', marginTop: '4px' }}>&#x2713; Expense type selected</div>
                )}
              </div>

              {/* Apply button */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleMultiAssign}
                  disabled={maApplying || maSelectedIds.size === 0 || ((!maJobId || !maJobTaskId) && !maRateCodeValue)}
                  style={{ fontSize: '12px' }}
                >
                  {maApplying ? 'Applying...' : `Assign to ${maSelectedIds.size} Expense(s)`}
                </button>
                <span style={{ fontSize: '11px', color: 'var(--gray-500)' }}>
                  {(maJobId && maJobTaskId ? 'Job/Task' : '') + (maJobId && maJobTaskId && maRateCodeValue ? ' + ' : '') + (maRateCodeValue ? 'Type' : '') || 'Select job/task or type above'}
                </span>
              </div>
            </div>
          )}

          {summaryEntries.length > 0 && (
              <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {multiAssignMode ? (
                  <>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={maSelectedIds.size > 0 && maSelectedIds.size === (() => {
                        let cnt = 0;
                        summaryEntries.forEach(e => { cnt++; cnt += unsubmittedEntries.filter(s => s.HeaderEntryID === e.ExpenseEntryID).length; });
                        return cnt;
                      })()} onChange={maToggleSelectAll} />
                      Select All
                    </label>
                    <span style={{ fontSize: '12px', color: 'var(--gray-500)' }}>{maSelectedIds.size} selected</span>
                  </>
                ) : (
                  <>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={selectedIds.size > 0 && selectedIds.size === (() => {
                        let cnt = 0;
                        summaryEntries.filter(e => isEntryComplete(e)).forEach(e => {
                          cnt++;
                          cnt += unsubmittedEntries.filter(s => s.HeaderEntryID === e.ExpenseEntryID).length;
                        });
                        return cnt;
                      })()} onChange={toggleSelectAll} />
                      Select All Complete
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                      {selectedIds.size > 0 && (() => {
                        const headerIds = [...selectedIds].filter(id => !unsubmittedEntries.find(e => e.ExpenseEntryID === id)?.HeaderEntryID);
                        const total = headerIds.reduce((sum, id) => {
                          const entry = unsubmittedEntries.find(e => e.ExpenseEntryID === id);
                          return sum + (parseFloat(entry?.TotalPrice) || 0);
                        }, 0);
                        return (
                          <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-700)', whiteSpace: 'nowrap' }}>
                            Checked Total: {formatCurrency(total)}
                          </span>
                        );
                      })()}
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleSubmitExpenses}
                        disabled={submitting || selectedIds.size === 0}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {submitting ? 'Submitting...' : `Submit ${[...selectedIds].filter(id => !unsubmittedEntries.find(e => e.ExpenseEntryID === id)?.HeaderEntryID).length} Expense(s)`}
                      </button>
                    </div>
                  </>
                )}
              </div>
          )}
          </div>{/* end .expense-summary-sticky */}

          {summaryEntries.length === 0 ? (
            <div className="empty-state"><p>No unsubmitted expenses.</p></div>
          ) : (
              <div className="card" style={{ padding: '0' }}>
                {sortedUnsubmitted.map((e) => {
                  const complete = isEntryComplete(e);
                  const missing = getMissingFields(e);
                  const subs = unsubmittedEntries.filter(s => s.HeaderEntryID === e.ExpenseEntryID);
                  const isSplit = subs.length > 0;

                  return (
                    <div key={e.ExpenseEntryID}>
                      <div
                        style={{
                          padding: '10px 12px', borderBottom: isSplit ? 'none' : '1px solid var(--gray-100)',
                          display: 'flex', gap: '8px', alignItems: 'flex-start',
                          cursor: multiAssignMode ? 'default' : 'pointer',
                          background: isSplit ? '#f8fafc' : undefined,
                        }}
                        onClick={() => { if (!multiAssignMode) handleEditFromSubmit(e.ExpenseEntryID); }}
                      >
                        <input
                          type="checkbox"
                          checked={multiAssignMode ? maSelectedIds.has(e.ExpenseEntryID) : selectedIds.has(e.ExpenseEntryID)}
                          onChange={(ev) => { ev.stopPropagation(); multiAssignMode ? maToggleSelect(e.ExpenseEntryID) : toggleSelect(e.ExpenseEntryID); }}
                          onClick={(ev) => ev.stopPropagation()}
                          disabled={!multiAssignMode && !complete}
                          style={{ marginTop: '3px', width: '16px', height: '16px', cursor: (multiAssignMode || complete) ? 'pointer' : 'not-allowed', opacity: (multiAssignMode || complete) ? 1 : 0.5 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: 500, display: 'flex', gap: '6px', alignItems: 'center' }}>
                            {e.Description || <span style={{ color: 'var(--danger)', fontWeight: 600 }}>[No Description]</span>}
                            {isSplit && <span style={{ fontSize: '10px', background: 'var(--primary, #2563eb)', color: 'white', padding: '1px 5px', borderRadius: '3px' }}>SPLIT</span>}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>
                            {e.EntryDate ? displayDate(e.EntryDate) : <span style={{ color: 'var(--danger)', fontWeight: 600 }}>[No Date]</span>}
                            {!isSplit && (
                              <>
                                {' | '}
                                {e.JobID ? `${e.JobID}/${e.JobTaskID}` : <span style={{ color: 'var(--danger)', fontWeight: 600 }}>[No Job/Job Task]</span>}
                                {' | '}
                                {e.RateCodeName || <span style={{ color: 'var(--danger)', fontWeight: 600 }}>[No Type]</span>}
                              </>
                            )}
                          </div>
                          {!complete && missing.length > 0 && (
                            <div style={{ fontSize: '11px', color: 'var(--danger)', fontWeight: 600, marginTop: '2px' }}>
                              {missing.join('  ')}
                            </div>
                          )}
                        </div>
                        <div
                          style={{ fontWeight: 600, fontSize: '14px', whiteSpace: 'nowrap', textAlign: 'right', cursor: multiAssignMode ? 'pointer' : undefined }}
                          onClick={multiAssignMode ? (ev) => { ev.stopPropagation(); handleEditFromSubmit(e.ExpenseEntryID); } : undefined}
                        >
                          {formatCurrency(e.TotalPrice)}
                          {(e.ReceiptCount > 0 || receipts.some(rc => rc.ExpenseEntryID === e.ExpenseEntryID)) && (
                            <div style={{ fontSize: '10px', color: '#16a34a', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '2px', marginTop: '2px' }}>
                              <span style={{ fontSize: '12px' }}>&#x2713;</span> Receipt
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Sub-entries for split groups */}
                      {isSplit && (
                        <div style={{ padding: '0 12px 8px 40px', borderBottom: '1px solid var(--gray-200)', background: '#f8fafc' }}>
                          {subs.map((sub) => (
                            <div
                              key={sub.ExpenseEntryID}
                              style={{
                                fontSize: '12px', padding: '4px 0', borderBottom: '1px solid var(--gray-100)',
                                display: 'flex', gap: '8px', cursor: multiAssignMode ? 'default' : 'pointer',
                              }}
                              onClick={() => { if (!multiAssignMode) handleEditFromSubmit(e.ExpenseEntryID); }}
                            >
                              <div style={{ flex: 1, color: 'var(--gray-600)' }}>
                                {sub.JobID ? `${sub.JobID}/${sub.JobTaskID}` : '-'}
                                {' | '}
                                {sub.RateCodeName || '-'}
                                {sub.Description ? ` | ${sub.Description}` : ''}
                              </div>
                              <div style={{ fontWeight: 500 }}>{formatCurrency(sub.TotalPrice)}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

          )}

          {/* Debug panel for credit card import */}
          {debugMode && creditCardDebugLogs.length > 0 && (
            <div style={{ marginTop: '16px', border: '1px solid var(--warning, #f59e0b)', borderRadius: '6px', background: '#fffbeb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #fde68a' }}>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#92400e' }}>Debug Log (Credit Card Import)</span>
                <button
                  className="btn btn-sm btn-outline"
                  onClick={() => setCreditCardDebugLogs([])}
                  style={{ fontSize: '11px', padding: '2px 6px' }}
                >Clear</button>
              </div>
              <div style={{ padding: '8px 12px', maxHeight: '300px', overflowY: 'auto' }}>
                {creditCardDebugLogs.map((log, i) => (
                  <div key={i} style={{ marginBottom: '4px', fontSize: '12px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    <span style={{ color: '#92400e', fontWeight: 600 }}>[{log.step}]</span>{' '}
                    {log.error && <span style={{ color: '#dc2626' }}>Error: {log.error}</span>}
                    {log.endpoint && <span>Endpoint: {log.endpoint}</span>}
                    {log.recordCount != null && <span>Records: {log.recordCount}</span>}
                    {log.keys && <span>Keys: {log.keys.join(', ')}</span>}
                    {log.transactionBatchID && <span>TxnID: {log.transactionBatchID}</span>}
                    {log.amount != null && <span> Amount: ${log.amount}</span>}
                    {log.supplierName && <span> Supplier: {log.supplierName}</span>}
                    {log.imported != null && <span>Imported: {log.imported}, Skipped: {log.skipped}</span>}
                    {log.durationMs != null && <span> ({log.durationMs}ms)</span>}
                    {log.totalDurationMs != null && <span> Total: {log.totalDurationMs}ms</span>}
                    {log.message && <span>{log.message}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Debug panel for expense submission */}
          {debugMode && submitDebugLogs && submitDebugLogs.length > 0 && (
            <div style={{ marginTop: '16px', border: '1px solid var(--warning, #f59e0b)', borderRadius: '6px', background: '#fffbeb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #fde68a' }}>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#92400e' }}>Debug Log (Expense Submission)</span>
                <button
                  className="btn btn-sm btn-outline"
                  onClick={() => setSubmitDebugLogs(null)}
                  style={{ fontSize: '11px', padding: '2px 6px' }}
                >Clear</button>
              </div>
              <div style={{ padding: '8px 12px', maxHeight: '400px', overflowY: 'auto' }}>
                {submitDebugLogs.map((log, i) => (
                  <div key={i} style={{ marginBottom: '6px', fontSize: '12px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    <span style={{ color: '#92400e', fontWeight: 600 }}>[{log.type}]</span>{' '}
                    {log.error && <span style={{ color: '#dc2626' }}>Error: {log.error}</span>}
                    {log.status && <span>HTTP {log.status} </span>}
                    {log.url && <span>{log.url} </span>}
                    {log.lineNo && <span>Line #{log.lineNo} </span>}
                    {log.request && (
                      <div style={{ color: '#374151', marginLeft: '8px', marginTop: '2px' }}>
                        Request: {JSON.stringify(log.request, null, 2)}
                      </div>
                    )}
                    {log.data && (
                      <div style={{ color: '#374151', marginLeft: '8px', marginTop: '2px' }}>
                        Response: {JSON.stringify(log.data, null, 2)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Debug panel for expense report reopen */}
          {debugMode && reopenDebugLogs && reopenDebugLogs.length > 0 && (
            <div style={{ marginTop: '16px', border: '1px solid var(--warning, #f59e0b)', borderRadius: '6px', background: '#fffbeb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #fde68a' }}>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#92400e' }}>Debug Log (Expense Report Reopen)</span>
                <button
                  className="btn btn-sm btn-outline"
                  onClick={() => setReopenDebugLogs(null)}
                  style={{ fontSize: '11px', padding: '2px 6px' }}
                >Clear</button>
              </div>
              <div style={{ padding: '8px 12px', maxHeight: '400px', overflowY: 'auto' }}>
                {reopenDebugLogs.map((log, i) => (
                  <div key={i} style={{ marginBottom: '6px', fontSize: '12px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    <span style={{ color: '#92400e', fontWeight: 600 }}>[{log.step}]</span>{' '}
                    {log.error && <span style={{ color: '#dc2626' }}>Error: {log.error}</span>}
                    {log.status && <span>HTTP {log.status} </span>}
                    {log.url && <span>{log.url} </span>}
                    {log.endpoint && <span>Endpoint: {log.endpoint} </span>}
                    {log.durationMs != null && <span>({log.durationMs}ms) </span>}
                    {log.dbStatus && <span>DB: {log.dbStatus} </span>}
                    {log.navStatus && <span>NAV: {log.navStatus} </span>}
                    {log.category && <span>Category: {log.category} </span>}
                    {log.payload && (
                      <div style={{ color: '#374151', marginLeft: '8px', marginTop: '2px' }}>
                        Request: {JSON.stringify(log.payload, null, 2)}
                      </div>
                    )}
                    {log.response && (
                      <div style={{ color: '#374151', marginLeft: '8px', marginTop: '2px' }}>
                        Response: {JSON.stringify(log.response, null, 2)}
                      </div>
                    )}
                    {log.navCheckDebug && (
                      <div style={{ color: '#374151', marginLeft: '8px', marginTop: '2px' }}>
                        NAV Check: {JSON.stringify(log.navCheckDebug, null, 2)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          </>}
        </div>
      )}

      {/* ============================================================
          REPORTS TAB
          ============================================================ */}
      {activeTab === 'reports' && (
        <div style={{ padding: '16px' }}>
          {/* Debug panel for expense report status/payment sync */}
          {debugMode && syncDebugLogs && syncDebugLogs.length > 0 && (
            <div style={{ marginBottom: '16px', border: '1px solid var(--warning, #f59e0b)', borderRadius: '6px', background: '#fffbeb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #fde68a' }}>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#92400e' }}>Debug Log (Status &amp; Payment Sync)</span>
                <button
                  className="btn btn-sm btn-outline"
                  onClick={() => setSyncDebugLogs([])}
                  style={{ fontSize: '11px', padding: '2px 6px' }}
                >Clear</button>
              </div>
              <div style={{ padding: '8px 12px', maxHeight: '400px', overflowY: 'auto' }}>
                {syncDebugLogs.map((log, i) => (
                  <div key={i} style={{ marginBottom: '4px', fontSize: '12px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    <span style={{ color: '#92400e', fontWeight: 600 }}>[{log.step}]</span>{' '}
                    {log.error && <span style={{ color: '#dc2626' }}>Error: {log.error} </span>}
                    {log.count != null && <span>count={log.count} </span>}
                    {log.hasPaidDateColumn != null && <span>hasPaidDateColumn={String(log.hasPaidDateColumn)} </span>}
                    {log.navServerDown != null && <span>navServerDown={String(log.navServerDown)} </span>}
                    {log.expNo && <span>exp={log.expNo} </span>}
                    {log.status && <span>status={log.status} </span>}
                    {log.paid != null && <span>paid={String(log.paid)} </span>}
                    {log.paidDate && <span>paidDate={String(log.paidDate)} </span>}
                    {log.reason && <span>reason={log.reason} </span>}
                    {log.newStatus && <span>newStatus={log.newStatus} </span>}
                    {(log.reports || log.navRecord) && (
                      <div style={{ color: '#374151', marginLeft: '8px', marginTop: '2px' }}>
                        {JSON.stringify(log.reports || log.navRecord)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {selectedReport ? (
            <>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => setSelectedReport(null)}
                style={{ marginBottom: '12px' }}
              >
                &larr; Back to Reports
              </button>

              <div className="card">
                <div className="card-header">
                  <span className="card-title">
                    Report {selectedReport.ExpenseReportNumber || selectedReport.ExpenseReportID.substring(0, 8)}
                  </span>
                  <span className={`ts-status ${reportStatusClass(selectedReport)}`}>
                    {reportStatusLabel(selectedReport)}
                  </span>
                </div>
                <div style={{ fontSize: '13px', color: 'var(--gray-500)', marginBottom: '12px' }}>
                  Created: {displayDate(selectedReport.CreatedAt)}
                </div>

                {(() => {
                  const allEntries = selectedReport.entries || [];
                  const subEntryIds = new Set(allEntries.filter(e => e.HeaderEntryID).map(e => e.ExpenseEntryID));
                  // Show only top-level entries (headers + standalone); sub-entries rendered under their header
                  const topLevel = allEntries.filter(e => !e.HeaderEntryID);
                  return topLevel.map((e) => {
                    const subs = allEntries.filter(s => s.HeaderEntryID === e.ExpenseEntryID);
                    const isSplit = subs.length > 0;
                    return (
                      <div key={e.ExpenseEntryID} style={{ borderBottom: '2px solid var(--gray-300)' }}>
                        <div style={{
                          padding: '10px 0',
                          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                          background: isSplit ? '#f8fafc' : undefined,
                          paddingLeft: isSplit ? '8px' : undefined,
                          paddingRight: isSplit ? '8px' : undefined,
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '13px', fontWeight: 500, display: 'flex', gap: '6px', alignItems: 'center' }}>
                              {e.Description || 'No description'}
                              {isSplit && <span style={{ fontSize: '10px', background: 'var(--primary, #2563eb)', color: 'white', padding: '1px 5px', borderRadius: '3px' }}>SPLIT</span>}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>
                              {displayDate(e.EntryDate)}
                              {!isSplit && (
                                <>
                                  {' | '}{e.JobID}/{e.JobTaskID} | {e.RateCodeName || ''}
                                </>
                              )}
                              {e.ReceiptCount > 0 && ` | ${e.ReceiptCount} receipt(s)`}
                            </div>
                          </div>
                          <div style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {formatCurrency(e.TotalPrice)}
                          </div>
                        </div>
                        {!isSplit && (
                          <div style={{ paddingBottom: '8px' }}>
                            {renderAttachedReceipts(e)}
                            {renderAddReceiptControl(e)}
                          </div>
                        )}
                        {isSplit && (
                          <div style={{ padding: '0 8px 8px 32px', background: '#f8fafc' }}>
                            {subs.map((sub) => (
                              <div key={sub.ExpenseEntryID} style={{ padding: '4px 0', borderBottom: '1px solid var(--gray-100)' }}>
                                <div style={{ fontSize: '12px', display: 'flex', gap: '8px' }}>
                                  <div style={{ flex: 1, color: 'var(--gray-600)' }}>
                                    {sub.JobID ? `${sub.JobID}/${sub.JobTaskID}` : '-'}
                                    {' | '}
                                    {sub.RateCodeName || '-'}
                                    {sub.Description ? ` | ${sub.Description}` : ''}
                                  </div>
                                  <div style={{ fontWeight: 500 }}>{formatCurrency(sub.TotalPrice)}</div>
                                </div>
                              </div>
                            ))}
                            {/* Receipts for a split are attached to the header and posted to every split line */}
                            <div style={{ paddingTop: '8px', marginLeft: '-24px' }}>
                              {renderAttachedReceipts(e)}
                              {renderAddReceiptControl(e)}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}

                <div style={{ marginTop: '12px', textAlign: 'right', fontWeight: 700, fontSize: '16px' }}>
                  Total: {formatCurrency(selectedReport.entries?.filter(e => !e.HeaderEntryID).reduce((s, e) => s + (parseFloat(e.TotalPrice) || 0), 0))}
                </div>

                <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                  <button
                    className="btn btn-outline btn-block"
                    disabled={pdfLoading}
                    onClick={() => openPdf(api.getExpenseReportPdfUrl(selectedReport.ExpenseReportID))}
                  >
                    {pdfLoading ? 'Generating...' : 'View PDF Report'}
                  </button>
                </div>

                {selectedReport.Status !== 'Approved' && (
                  <div style={{ marginTop: '8px' }}>
                    <button
                      className="btn btn-danger btn-block"
                      onClick={() => handleUnsubmit(selectedReport.ExpenseReportID)}
                    >
                      Reopen Report
                    </button>
                  </div>
                )}

                {/* Debug panel for expense report reopen (reports tab) */}
                {debugMode && reopenDebugLogs && reopenDebugLogs.length > 0 && (
                  <div style={{ marginTop: '16px', border: '1px solid var(--warning, #f59e0b)', borderRadius: '6px', background: '#fffbeb' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #fde68a' }}>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#92400e' }}>Debug Log (Expense Report Reopen)</span>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => setReopenDebugLogs(null)}
                        style={{ fontSize: '11px', padding: '2px 6px' }}
                      >Clear</button>
                    </div>
                    <div style={{ padding: '8px 12px', maxHeight: '400px', overflowY: 'auto' }}>
                      {reopenDebugLogs.map((log, i) => (
                        <div key={i} style={{ marginBottom: '6px', fontSize: '12px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                          <span style={{ color: '#92400e', fontWeight: 600 }}>[{log.step}]</span>{' '}
                          {log.error && <span style={{ color: '#dc2626' }}>Error: {log.error}</span>}
                          {log.status && <span>HTTP {log.status} </span>}
                          {log.url && <span>{log.url} </span>}
                          {log.endpoint && <span>Endpoint: {log.endpoint} </span>}
                          {log.durationMs != null && <span>({log.durationMs}ms) </span>}
                          {log.dbStatus && <span>DB: {log.dbStatus} </span>}
                          {log.navStatus && <span>NAV: {log.navStatus} </span>}
                          {log.category && <span>Category: {log.category} </span>}
                          {log.payload && (
                            <div style={{ color: '#374151', marginLeft: '8px', marginTop: '2px' }}>
                              Request: {JSON.stringify(log.payload, null, 2)}
                            </div>
                          )}
                          {log.response && (
                            <div style={{ color: '#374151', marginLeft: '8px', marginTop: '2px' }}>
                              Response: {JSON.stringify(log.response, null, 2)}
                            </div>
                          )}
                          {log.navCheckDebug && (
                            <div style={{ color: '#374151', marginLeft: '8px', marginTop: '2px' }}>
                              NAV Check: {JSON.stringify(log.navCheckDebug, null, 2)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Debug panel for report receipt attach/remove NAV calls */}
                {debugMode && receiptNavDebug && (
                  <div style={{ marginTop: '16px', border: '1px solid var(--warning, #f59e0b)', borderRadius: '6px', background: '#fffbeb' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #fde68a' }}>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#92400e' }}>Debug Log (Receipt {receiptNavDebug.op})</span>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => setReceiptNavDebug(null)}
                        style={{ fontSize: '11px', padding: '2px 6px' }}
                      >Clear</button>
                    </div>
                    <div style={{ padding: '8px 12px', maxHeight: '400px', overflowY: 'auto' }}>
                      {receiptNavDebug.error && (
                        <div style={{ marginBottom: '8px', fontSize: '12px', color: '#dc2626', fontWeight: 600 }}>
                          Error: {receiptNavDebug.error}
                        </div>
                      )}
                      {(receiptNavDebug.results || []).length === 0 && !receiptNavDebug.error && (
                        <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>No NAV calls were made.</div>
                      )}
                      {(receiptNavDebug.results || []).map((log, i) => (
                        <div key={i} style={{ marginBottom: '8px', fontSize: '12px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', borderTop: i > 0 ? '1px solid #fde68a' : 'none', paddingTop: i > 0 ? '6px' : 0 }}>
                          <span style={{ color: '#92400e', fontWeight: 600 }}>[{log.type}{log.lineNo != null ? ` line #${log.lineNo}` : ''}]</span>{' '}
                          {log.ok === true && <span style={{ color: '#16a34a' }}>OK </span>}
                          {log.ok === false && <span style={{ color: '#dc2626' }}>FAILED </span>}
                          {log.status != null && <span>HTTP {log.status} </span>}
                          {log.error && <span style={{ color: '#dc2626' }}>Error: {log.error}</span>}
                          {log.url && <div style={{ color: '#6b7280', marginTop: '2px' }}>{log.url}</div>}
                          {log.request && (
                            <div style={{ color: '#374151', marginLeft: '8px', marginTop: '2px' }}>
                              Request: {JSON.stringify(log.request, null, 2)}
                            </div>
                          )}
                          {log.data && (
                            <div style={{ color: '#374151', marginLeft: '8px', marginTop: '2px' }}>
                              Response: {JSON.stringify(log.data, null, 2)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Action buttons */}
              {!loadingReports && reports.length > 0 && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  {checkedReportIds.size > 0 && (
                    <button
                      className="btn btn-primary btn-sm"
                      disabled={pdfLoading}
                      onClick={() => openPdf(api.getCombinedReportPdfUrl([...checkedReportIds]))}
                    >
                      {pdfLoading ? 'Generating...' : `Combined PDF (${checkedReportIds.size})`}
                    </button>
                  )}
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => {
                      setShowCustomReport(!showCustomReport);
                      if (!showCustomReport && reportJobs.length === 0) {
                        api.getReportJobs().then(setReportJobs).catch(() => {});
                      }
                    }}
                  >
                    {showCustomReport ? 'Hide' : 'Custom Report'}
                  </button>
                  {isAdmin && (
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => {
                        setShowAdminReport(!showAdminReport);
                        if (!showAdminReport && adminReportUsers.length === 0) {
                          api.getAdminReportUsers().then(setAdminReportUsers).catch(() => {});
                          api.getAdminReportJobs().then(setAdminReportJobs).catch(() => {});
                        }
                      }}
                    >
                      {showAdminReport ? 'Hide Admin' : 'Admin Reports'}
                    </button>
                  )}
                </div>
              )}

              {pdfError && (
                <Banner kind="err" onClose={() => setPdfError(null)} style={{ margin: '0 0 12px' }}>{pdfError}</Banner>
              )}

              {/* Custom report filters */}
              {showCustomReport && (
                <div className="card" style={{ marginBottom: '12px' }}>
                  <div className="card-header"><span className="card-title">Custom Report</span></div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div>
                      <label style={{ fontSize: '12px', display: 'block', marginBottom: '2px' }}>Start Date</label>
                      <input type="date" className="form-control" style={{ width: '140px' }}
                        value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} />
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', display: 'block', marginBottom: '2px' }}>End Date</label>
                      <input type="date" className="form-control" style={{ width: '140px' }}
                        value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} />
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', display: 'block', marginBottom: '2px' }}>Job (optional)</label>
                      <select className="form-control" style={{ width: '200px' }}
                        value={customJobId} onChange={(e) => setCustomJobId(e.target.value)}>
                        <option value="">All jobs</option>
                        {reportJobs.map(j => (
                          <option key={j.JobID} value={j.JobID}>{j.JobID} - {j.JobName}</option>
                        ))}
                      </select>
                    </div>
                    {(() => {
                      const hasDateRange = customStartDate && customEndDate;
                      const hasJob = !!customJobId;
                      const canGenerate = hasDateRange || hasJob;
                      return (
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={!canGenerate || pdfLoading}
                          onClick={() => openPdf(api.getCustomReportPdfUrl({ startDate: customStartDate || undefined, endDate: customEndDate || undefined, jobId: customJobId || undefined }))}
                        >
                          {pdfLoading ? 'Generating...' : 'Generate PDF'}
                        </button>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Admin custom report filters */}
              {showAdminReport && isAdmin && (
                <div className="card" style={{ marginBottom: '12px' }}>
                  <div className="card-header"><span className="card-title">Admin Custom Report</span></div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div>
                      <label style={{ fontSize: '12px', display: 'block', marginBottom: '2px' }}>User</label>
                      <select className="form-control" style={{ width: '200px' }}
                        value={adminUserId} onChange={(e) => {
                          setAdminUserId(e.target.value);
                          if (e.target.value) {
                            api.getAdminReportJobs(e.target.value).then(setAdminReportJobs).catch(() => {});
                          } else {
                            api.getAdminReportJobs().then(setAdminReportJobs).catch(() => {});
                          }
                        }}>
                        <option value="">All users</option>
                        {adminReportUsers.map(u => (
                          <option key={u.UserID} value={u.UserID}>{u.DisplayName}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', display: 'block', marginBottom: '2px' }}>Start Date</label>
                      <input type="date" className="form-control" style={{ width: '140px' }}
                        value={adminStartDate} onChange={(e) => setAdminStartDate(e.target.value)} />
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', display: 'block', marginBottom: '2px' }}>End Date</label>
                      <input type="date" className="form-control" style={{ width: '140px' }}
                        value={adminEndDate} onChange={(e) => setAdminEndDate(e.target.value)} />
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', display: 'block', marginBottom: '2px' }}>Job</label>
                      <select className="form-control" style={{ width: '200px' }}
                        value={adminJobId} onChange={(e) => setAdminJobId(e.target.value)}>
                        <option value="">All jobs</option>
                        {adminReportJobs.map(j => (
                          <option key={j.JobID} value={j.JobID}>{j.JobID} - {j.JobName}</option>
                        ))}
                      </select>
                    </div>
                    {(() => {
                      const hasDateRange = adminStartDate && adminEndDate;
                      const hasJob = !!adminJobId;
                      const canGenerate = hasDateRange || hasJob;
                      return (
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={!canGenerate || pdfLoading}
                          onClick={() => openPdf(api.getAdminCustomReportPdfUrl({ startDate: adminStartDate || undefined, endDate: adminEndDate || undefined, jobId: adminJobId || undefined, userId: adminUserId || undefined }))}
                        >
                          {pdfLoading ? 'Generating...' : 'Generate PDF'}
                        </button>
                      );
                    })()}
                  </div>
                </div>
              )}

              {loadingReports ? (
                <div className="loading"><div className="spinner" />Loading reports...</div>
              ) : reports.length === 0 ? (
                <div className="empty-state"><p>No expense reports yet.</p></div>
              ) : (
                <>
                  <div className="card" style={{ padding: '0' }}>
                    {reports.map((r) => (
                      <div
                        key={r.ExpenseReportID}
                        className="ts-list-item"
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                      >
                        <input
                          type="checkbox"
                          checked={checkedReportIds.has(r.ExpenseReportID)}
                          onChange={(e) => {
                            e.stopPropagation();
                            setCheckedReportIds(prev => {
                              const next = new Set(prev);
                              if (next.has(r.ExpenseReportID)) next.delete(r.ExpenseReportID);
                              else next.add(r.ExpenseReportID);
                              return next;
                            });
                          }}
                          style={{ flexShrink: 0 }}
                        />
                        <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => handleViewReport(r.ExpenseReportID)}>
                          <div className="ts-period">
                            {r.ExpenseReportNumber || r.ExpenseReportID.substring(0, 8)}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--gray-400)' }}>
                            {displayDate(r.CreatedAt)} | {r.EntryCount} items | {formatCurrency(r.TotalAmount)}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <button
                            className="btn btn-outline btn-sm"
                            style={{ fontSize: '11px', padding: '2px 8px' }}
                            disabled={pdfLoading}
                            onClick={(e) => { e.stopPropagation(); openPdf(api.getExpenseReportPdfUrl(r.ExpenseReportID)); }}
                          >PDF</button>
                          <span className={`ts-status ${reportStatusClass(r)}`}>
                            {reportStatusLabel(r)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {reportsTotal > 25 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '12px' }}>
                      <button
                        className="btn btn-outline btn-sm"
                        disabled={reportsPage <= 1}
                        onClick={() => refreshReports(reportsPage - 1)}
                      >Prev</button>
                      <span style={{ fontSize: '13px', padding: '6px' }}>
                        Page {reportsPage} of {Math.ceil(reportsTotal / 25)}
                      </span>
                      <button
                        className="btn btn-outline btn-sm"
                        disabled={reportsPage >= Math.ceil(reportsTotal / 25)}
                        onClick={() => refreshReports(reportsPage + 1)}
                      >Next</button>
                    </div>
                  )}
                </>
              )}

              {loadingReportDetail && (
                <div className="loading" style={{ marginTop: '12px' }}><div className="spinner" />Loading report...</div>
              )}
            </>
          )}
        </div>
      )}

      {/* Auto-match results modal */}
      {autoMatchResults && (
        <div
          className="modal-overlay"
          onClick={() => { setAutoMatchResults(null); setAutoMatchSelected(new Set()); }}
          style={{ zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: '90vw', maxWidth: '600px', maxHeight: '80vh', background: 'white', borderRadius: '8px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <div style={{ padding: '16px', borderBottom: '1px solid var(--gray-200)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '16px', fontWeight: 600 }}>Auto-Match Results</span>
                <IconButton icon="close" title="Close" onClick={() => { setAutoMatchResults(null); setAutoMatchSelected(new Set()); }} />
              </div>
              <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '4px' }}>
                Found {autoMatchResults.matches.length} proposed match{autoMatchResults.matches.length !== 1 ? 'es' : ''} out of {autoMatchResults.stats.unmatchedReceipts} unmatched receipt{autoMatchResults.stats.unmatchedReceipts !== 1 ? 's' : ''} and {autoMatchResults.stats.unmatchedEntries} unmatched entr{autoMatchResults.stats.unmatchedEntries !== 1 ? 'ies' : 'y'}.
              </div>
              <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                <button
                  className="btn btn-sm btn-outline"
                  onClick={() => setAutoMatchSelected(new Set(autoMatchResults.matches.map((_, i) => i)))}
                  style={{ fontSize: '11px' }}
                >Select All</button>
                <button
                  className="btn btn-sm btn-outline"
                  onClick={() => setAutoMatchSelected(new Set())}
                  style={{ fontSize: '11px' }}
                >Select None</button>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
              {autoMatchResults.matches.map((match, i) => (
                <label key={i} style={{
                  display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 0',
                  borderBottom: '1px solid var(--gray-100)', cursor: 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={autoMatchSelected.has(i)}
                    onChange={() => {
                      setAutoMatchSelected(prev => {
                        const next = new Set(prev);
                        if (next.has(i)) next.delete(i);
                        else next.add(i);
                        return next;
                      });
                    }}
                    style={{ marginTop: '3px', flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 500 }}>
                      {match.receiptName}
                      {match.receiptMerchant && (
                        <span style={{ fontWeight: 400, color: 'var(--gray-500)', marginLeft: '4px' }}>
                          ({match.receiptMerchant})
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--gray-600)', marginTop: '2px' }}>
                      {displayDate(match.entryDate)}
                      {match.entryJobName ? ` | ${match.entryJobName}` : ''}
                      {match.entryDescription ? ` | ${match.entryDescription}` : ''}
                    </div>
                  </div>
                  <div style={{ fontWeight: 600, fontSize: '13px', flexShrink: 0 }}>
                    {formatCurrency(match.entryAmount)}
                  </div>
                </label>
              ))}
            </div>
            <div style={{ padding: '12px 16px', borderTop: '1px solid var(--gray-200)', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-outline"
                onClick={() => { setAutoMatchResults(null); setAutoMatchSelected(new Set()); }}
              >Cancel</button>
              <button
                className="btn btn-primary"
                disabled={autoMatchSelected.size === 0 || applyingMatches}
                onClick={handleApplyAutoMatches}
              >
                {applyingMatches ? 'Applying...' : `Apply ${autoMatchSelected.size} Match${autoMatchSelected.size !== 1 ? 'es' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Split picker modal */}
      {copySplitOptions && (
        <div
          className="modal-overlay"
          onClick={() => setCopySplitOptions(null)}
          style={{ zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: '90vw', maxWidth: '550px', maxHeight: '80vh', background: 'white', borderRadius: '8px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <div style={{ padding: '16px', borderBottom: '1px solid var(--gray-200)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '16px', fontWeight: 600 }}>Copy Split From...</span>
                <IconButton icon="close" title="Close" onClick={() => setCopySplitOptions(null)} />
              </div>
              <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '4px' }}>
                Select a previous split to copy its job/task and expense type assignments. Amounts will not be copied.
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
              {copySplitOptions.map((split) => (
                <div
                  key={split.headerEntryId}
                  onClick={() => handleApplyCopySplit(split)}
                  style={{
                    padding: '10px', marginBottom: '6px', borderRadius: '6px', cursor: 'pointer',
                    border: '1px solid var(--gray-200)', background: 'var(--gray-50)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.borderColor = 'var(--primary, #2563eb)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--gray-50)'; e.currentTarget.style.borderColor = 'var(--gray-200)'; }}
                >
                  <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>
                    {split.entryDate ? displayDate(split.entryDate) : 'No date'}
                    {' — '}
                    {split.description || 'No description'}
                    <span style={{ fontWeight: 400, color: 'var(--gray-500)', marginLeft: '6px' }}>
                      ({formatCurrency(split.totalPrice)})
                    </span>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--gray-600)' }}>
                    {split.subEntries.map((s, i) => {
                      const closed = (s.jobId && !s.jobActive) || (s.jobTaskId && !s.taskActive);
                      return (
                        <div key={i} style={{ display: 'flex', gap: '8px', padding: '2px 0', opacity: closed ? 0.5 : 1 }}>
                          <span style={{ minWidth: '120px' }}>
                            {s.jobId ? `${s.jobId}/${s.jobTaskId || ''}` : '-'}
                            {closed && <span style={{ color: 'var(--danger, #dc2626)', marginLeft: '4px', fontSize: '10px' }}>(closed)</span>}
                          </span>
                          <span>{s.rateCodeName || '-'}</span>
                          <span style={{ marginLeft: 'auto' }}>{formatCurrency(s.subTotal)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding: '12px 16px', borderTop: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setCopySplitOptions(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt preview modal (shared across all tabs) */}
      {receiptPreviewUrl && (
        <div
          className="modal-overlay"
          onClick={() => { setReceiptPreviewUrl(null); setReceiptPreviewId(null); }}
          style={{ zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '90vw', maxHeight: '90vh', background: 'white', borderRadius: '8px', padding: '8px', position: 'relative' }}
          >
            <IconButton icon="close" title="Close" onClick={() => { setReceiptPreviewUrl(null); setReceiptPreviewId(null); }}
              style={{ position: 'absolute', top: '4px', right: '8px' }} />
            {receiptPreviewIsPdf ? (
              <iframe
                src={receiptPreviewUrl}
                title="Receipt preview"
                style={{ width: '80vw', height: '80vh', border: 'none' }}
              />
            ) : (
              <img
                src={receiptPreviewUrl}
                alt="Receipt"
                style={{ maxWidth: '85vw', maxHeight: '85vh', objectFit: 'contain' }}
              />
            )}
            {!receiptPreviewIsPdf && receiptPreviewId && (
              <div style={{ textAlign: 'center', marginTop: '8px' }}>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => { openReceiptEditor(receiptPreviewId); setReceiptPreviewUrl(null); setReceiptPreviewId(null); }}
                >Rotate / Crop</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Receipt image editor (rotate/crop) */}
      {editorReceiptId && (
        <ReceiptImageEditor
          imageUrl={receiptPreviewSrc(editorReceiptId)}
          onSave={handleSaveEditedImage}
          onCancel={() => setEditorReceiptId(null)}
        />
      )}
    </>
  );
}
